import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams } from 'react-router-dom';
import {
  Loader2, FileText, ScrollText, Calendar, Sparkles,
  Users, Briefcase, DollarSign, ShieldAlert, Scale, PenTool,
  Heart, CheckCircle2, Check, MapPin, ClipboardList, Printer,
  CreditCard, Building, Mail, Clock, Download, Award, Lock, ChevronDown, ChevronLeft, Eye, Pencil, ArrowRight
} from 'lucide-react';
import { toast } from 'sonner';
import * as Sentry from '@sentry/react';
import PayOptions from '../components/clientPay/PayOptions';
import LineItemsSummary from '../components/clientPay/LineItemsSummary';
import { paymentStatusFrom, isExtraCharge } from '../lib/paymentStatus';
import PlanningFormRenderer from '../components/planning/PlanningFormRenderer';
import { getPlanningSchema } from '../config/planningSchemas';
import ClientMessageWidget from '../components/ClientMessageWidget';
import SignaturePad from '../components/SignaturePad';
import confetti from 'canvas-confetti';
import { formatDate } from '../lib/date';
import { getDaysUntilEvent } from '../lib/eventRisk';
import { formatWeekday } from '../lib/datetime';
import { formatMoney } from '../lib/formatMoney';
import { resolveServicesIncluded } from '../lib/proposalDisplay';
import { linesFrom, linesToPricedHtml, linesToTitledHtml } from '../lib/serviceLines';
import { headingFor } from '../lib/proposalHeadings';
import ClientProgressBar from '../components/ui/ClientProgressBar';
import ProposalUrgencyNote from '../components/ui/ProposalUrgencyNote';
import {
  ClientMobileChrome, MobileStickyBar, IncludedList, InvestmentPanel,
  OptionalNote, PlanningTabs, DueCard, PayMethods, PaymentSchedule,
  buildSteps, fmtEventDate, fmtTimeRange, eventStartMs,
} from '../components/ui/ClientMobileScreens';
import ProposalAddOns from '../components/ui/ProposalAddOns';
import PackagesShowcase from '../components/ui/PackagesShowcase';
import ConfirmPaymentMethodDialog from '../components/clientPay/ConfirmPaymentMethodDialog';
import ContractUpgrades from '../components/ui/ContractUpgrades';
import SectionErrorBoundary from '../components/ui/SectionErrorBoundary';
import { sanitizeHtml, isHtmlContent } from '../lib/sanitizeHtml';
import EventCountdown from '../components/EventCountdown';
import '../styles/clientContractPrint.css';


const API = process.env.REACT_APP_BACKEND_URL;

// headingKey matches the same key in ProposalGenerator.jsx. The label beside it
// is the last resort only, for a record that carries no headings at all.
const PROPOSAL_SECTIONS = [
  { key: 'eventOverview',       label: 'Event Overview',       headingKey: 'overview',   icon: FileText },
  { key: 'servicesIncluded',    label: 'Services Included',    headingKey: 'services',   icon: Briefcase },
  { key: 'suggestedExperience', label: 'Suggested Experience', headingKey: 'experience', icon: Sparkles },
  { key: 'pricingStructure',    label: 'Pricing Structure',    headingKey: 'pricing',    icon: DollarSign },
  { key: 'closingStatement',    label: 'Closing Statement',    headingKey: 'closing',    icon: Heart },
];

const CONTRACT_SECTIONS = [  { key: 'parties',             label: 'Parties',              icon: Users },
  { key: 'eventDetails',       label: 'Event Details',        icon: Calendar },
  { key: 'servicesIncluded',   label: 'Services Included',    headingKey: 'services',    icon: Briefcase },
  { key: 'investment',         label: 'Investment',           icon: DollarSign },
  { key: 'paymentTerms',       label: 'Payment Terms',        icon: DollarSign },
  { key: 'cancellationPolicy', label: 'Cancellation Policy',  icon: ShieldAlert },
  { key: 'liabilityClause',   label: 'Liability Clause',     icon: Scale },
  { key: 'additionalTerms',   label: 'Additional Terms',     icon: Scale },
  { key: 'signatureSection',  label: 'Signature Section',    icon: PenTool },
];

// The legal prose a client does not need to wade through before signing. It
// is NOT dropped — it renders verbatim inside the "Read the full terms"
// accordion at the end of the contract.
const CONTRACT_LEGAL_KEYS = ['parties', 'eventDetails', 'paymentTerms', 'cancellationPolicy', 'liabilityClause', 'additionalTerms'];

function Section({ icon: Icon, label, content, collapsible = false }) {
  const [open, setOpen] = useState(!collapsible);
  if (!content) return null;
  return (
    <div
      className="rounded-xl overflow-hidden"
      style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.04)' }}
    >
      {collapsible ? (
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          className="w-full flex items-center gap-2 px-4 py-3 text-left transition-colors hover:bg-white/[0.02]"
          style={{ borderBottom: open ? '1px solid rgba(255,255,255,0.03)' : 'none' }}
          data-testid={`cv-section-toggle-${label}`}
        >
          <Icon className="w-3.5 h-3.5 text-[#D4AF37]/70" />
          <span className="text-[11px] font-semibold text-[#D4AF37]/90 tracking-wide uppercase">{label}</span>
          <ChevronDown className={`w-4 h-4 ml-auto text-[#D4AF37]/60 transition-transform duration-300 ${open ? 'rotate-180' : ''}`} />
        </button>
      ) : (
        <div className="flex items-center gap-2 px-4 py-3" style={{ borderBottom: '1px solid rgba(255,255,255,0.03)' }}>
          <Icon className="w-3.5 h-3.5 text-[#D4AF37]/70" />
          <span className="text-[11px] font-semibold text-[#D4AF37]/90 tracking-wide uppercase">{label}</span>
        </div>
      )}
      {(!collapsible || open) ? (
      <div className="px-4 py-3 cv-sec-body">
        {isHtmlContent(content) ? (
          <div
            className="min-w-0 max-w-full break-words text-[13px] text-white/55 leading-relaxed [&_p]:mb-2 [&_p:last-child]:mb-0 [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:my-2 [&_ol]:list-decimal [&_ol]:list-inside [&_ol]:pl-0 sm:[&_ol]:list-outside sm:[&_ol]:pl-5 [&_ol]:my-2 [&_li]:mb-1 [&_strong]:font-semibold [&_strong]:text-white/80 [&_b]:font-semibold [&_b]:text-white/80 [&_em]:italic [&_a]:text-[#D4AF37] [&_a]:underline [&_h1]:text-white/80 [&_h1]:font-semibold [&_h2]:text-white/80 [&_h2]:font-semibold [&_h3]:text-white/80 [&_h3]:font-semibold"
            dangerouslySetInnerHTML={{ __html: sanitizeHtml(content) }}
          />
        ) : (
          <p className="text-[13px] text-white/55 leading-relaxed whitespace-pre-wrap">{content}</p>
        )}
      </div>
      ) : (
        /* Collapsed: kept in the DOM but hidden, so the print stylesheet can
           open every term for a printed agreement. Identical on screen. */
        <div className="px-4 py-3 cv-sec-body" style={{ display: 'none' }}>
          {isHtmlContent(content) ? (
            <div
              className="min-w-0 max-w-full break-words text-[13px] text-white/55 leading-relaxed"
              dangerouslySetInnerHTML={{ __html: sanitizeHtml(content) }}
            />
          ) : (
            <p className="text-[13px] text-white/55 leading-relaxed whitespace-pre-wrap">{content}</p>
          )}
        </div>
      )}
    </div>
  );
}

function ApprovedBadge({ label, timestamp }) {
  return (
    <div
      className="rounded-xl px-5 py-4 flex items-center justify-between"
      style={{ background: 'rgba(34,197,94,0.06)', border: '1px solid rgba(34,197,94,0.12)' }}
      data-testid={`cv-${label.toLowerCase()}-approved-badge`}
    >
      <div className="flex items-center gap-2.5">
        <div
          className="w-8 h-8 rounded-lg flex items-center justify-center"
          style={{ background: 'rgba(34,197,94,0.12)' }}
        >
          <Check className="w-4 h-4 text-[#22C55E]" />
        </div>
        <div>
          <p className="text-[13px] font-semibold text-[#22C55E]/80">{label} Approved</p>
          {timestamp && (
            <p className="text-[10px] text-white/25 mt-0.5">
              {new Date(timestamp).toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
            </p>
          )}
        </div>
      </div>
      <CheckCircle2 className="w-5 h-5 text-[#22C55E]/40" />
    </div>
  );
}

/* ── ONE list of the documents this client may see ────────────────────────
   ROOT CAUSE this closes: the tab list and the document router were two
   independent answers to "which documents may this client see, and which is
   she looking at". The tab list dropped the Proposal tab once the proposal
   was APPROVED, while the router only redirected away from the proposal when
   it was skipped or never sent — an approved proposal is neither. A client who
   had approved her proposal and been sent her contract was parked on a
   document with no tab, with a one-item tab list that the strip then hid
   entirely, reading copy that told her to "open the Contract tab below".
   Ordering made it structural: the approval flags were declared BELOW the
   router, so it could not consider approval even in principle.

   These helpers are the single source for the tab strip, the default document,
   the redirect gate, the step copy and every line of copy that names a tab.
   They live at module scope so ordering inside the component cannot re-open
   the gap, and so the combinations are directly testable. */
/* ── ONE answer to "is this module skipped for this event" ────────────────
   ROOT CAUSE this closes: skip was wired per module BY HAND. `proposal` got
   its own `proposalSkipped` variable and was honoured; `planning` was gated
   by an unrelated set (`planningTriggered` / `planningRequired` /
   `planningPointer` / `planningDone`) that never consulted the skip list at
   all. A client whose Planning the admin had explicitly turned off was still
   shown a Planning tab, a "Planning Time" update panel, a
   "Next: Planning Details" button and a rail entry telling her to share her
   planning details — on the day of her event.

   Adding a `planningSkipped` next to `proposalSkipped` would have repeated
   the defect and the next module would have been missed the same way. So
   this is the ONLY way the question is answered, for every module, and no
   per-module "<name>Skipped" variable exists any more. A guard test fails the
   build if one is reintroduced.

   A skipped module is INVISIBLE to the client: no tab, no call-to-action, no
   update panel, no rail entry, no roadmap entry. Not greyed out, not labelled
   "skipped". The client never learns the step existed. */
export const CLIENT_JOURNEY_ORDER = ['proposal', 'contract', 'payment', 'planning'];

export function isModuleSkipped(data, moduleKey) {
  const entry = (((data || {}).skippedModules) || {})[moduleKey];
  return Boolean(entry && entry.skipped);
}

/* Every module the admin skipped, read from the same place as the single
   question above so the rail and the per-surface checks can never disagree. */
export function skippedModuleKeys(data) {
  const sm = (data || {}).skippedModules || {};
  return Object.keys(sm).filter((k) => isModuleSkipped(data, k));
}

/* The "what's next" pointer must never name a step that was turned off. If
   the immediate next step is skipped, walk forward to the first step that is
   neither skipped nor already done. If nothing is left, the journey is
   complete ('done') and the rail says nothing about a next step. */
export function resolveNextStep(nextKey, data, doneByKey) {
  if (!nextKey || nextKey === 'done') return nextKey ?? null;
  let i = CLIENT_JOURNEY_ORDER.indexOf(nextKey);
  if (i === -1) return nextKey;
  const done = doneByKey || {};
  for (; i < CLIENT_JOURNEY_ORDER.length; i += 1) {
    const k = CLIENT_JOURNEY_ORDER[i];
    if (!isModuleSkipped(data, k) && !done[k]) return k;
  }
  return 'done';
}

export function clientDocAccess(data) {
  const d = data || {};
  // Admin "Preview as client" and the read-only planner link exist to view
  // documents BEFORE they are sent. They keep that, and they keep it through
  // this one list rather than by bypassing it.
  const previewMode = Boolean(d.readOnly || d.previewOnly);
  const proposalApproved = d.proposalClientStatus === 'approved';
  const proposalChangesRequested = d.proposalClientStatus === 'changes_requested';
  const contractApproved = d.contractClientStatus === 'approved';
  const planningDone = Boolean(d.planningSubmitted);
  // What did this client actually receive? A document they were never sent
  // must not be shown, and a document they WERE sent must always be reachable.
  const proposalAvailable = Boolean(d.proposal) && (Boolean(d.proposalSentAt) || proposalApproved);
  const proposalSkipped = isModuleSkipped(d, 'proposal');
  const contractSkipped = isModuleSkipped(d, 'contract');
  // Reached THIS client. Approval implies delivery.
  const proposalDelivered = Boolean(d.proposal)
    && (Boolean(d.proposalSentAt) || proposalApproved);
  const contractDelivered = Boolean(d.contract)
    && (Boolean(d.contractSentAt) || contractApproved);
  // SETTLED = this step will never need anything from the client again.
  // Approved and skipped are the same fact to everyone downstream.
  const proposalSettled = proposalApproved || proposalSkipped;
  const contractSettled = contractApproved || contractSkipped;
  // IN PLAY = this step is part of THIS client's journey.
  // Preview exists to show what the client sees, so preview may relax the
  // "already delivered" half and NEVER the skip half.
  const proposalInPlay = !proposalSkipped
    && Boolean(d.proposal)
    && (previewMode || proposalDelivered);
  const contractInPlay = !contractSkipped
    && Boolean(d.contract)
    && (previewMode || contractDelivered || proposalApproved);
  // WAITING ON US = in play, but nothing has reached them yet.
  const docsPending = (proposalInPlay && !proposalDelivered)
    || (contractInPlay && !contractDelivered);
  // Planning is part of this client's journey only when the admin triggered
  // it AND did not skip it. Every planning surface reads this one value.
  const planningInPlay = Boolean(d.planningTriggered) && !isModuleSkipped(d, 'planning');

  // ── SEQUENTIAL GATE ───────────────────────────────────────────────────────
  // A client acts on ONE stage at a time, in order. The gate is derived from
  // the SAME completion state the admin pipeline and the stepper ticks read
  // (approved / approved / retainer paid) — no new flags — so the client's
  // locks and the admin's view can never disagree, and a reopened proposal or
  // an un-signed contract re-locks everything after it on the next render.
  // A skipped stage is not waited on: the chain walks past it to the previous
  // stage the client still owes us something on.
  const retainerPaid = Boolean(d.retainerPaid);
  // Any money already recorded means this stage is under way — never lock a
  // client out of a payment they have started or finished.
  const paymentStarted = retainerPaid || Boolean(d.paidInFull);
  const STAGE_IN_PLAY = {
    proposal: proposalInPlay,
    contract: contractInPlay,
    payment: !isModuleSkipped(d, 'payment'),
    planning: planningInPlay,
  };
  const STAGE_SETTLED = {
    proposal: proposalSettled,
    contract: contractSettled,
    payment: paymentStarted || isModuleSkipped(d, 'payment'),
    planning: planningDone,
  };
  const LOCK_HINTS = {
    proposal: 'Approve your proposal first.',
    contract: 'Sign your contract first.',
    payment: 'Complete your payment first.',
  };
  // The first earlier stage this client still owes us, or null when the stage
  // is theirs to act on. Preview and the read-only planner link are never
  // gated — they exist to show every stage.
  const blockerFor = (stageKey) => {
    if (previewMode) return null;
    const idx = CLIENT_JOURNEY_ORDER.indexOf(stageKey);
    for (let i = 0; i < idx; i += 1) {
      const k = CLIENT_JOURNEY_ORDER[i];
      if (isModuleSkipped(d, k)) continue;
      if (!STAGE_IN_PLAY[k]) continue;
      if (!STAGE_SETTLED[k]) return k;
    }
    return null;
  };

  const docTabs = [];
  if (proposalInPlay && !proposalApproved) {
    docTabs.push({ key: 'proposal', label: 'Proposal', icon: FileText, approved: proposalApproved });
  }
  if (contractInPlay) {
    const blocker = blockerFor('contract');
    docTabs.push({
      key: 'contract', label: 'Contract', icon: ScrollText, approved: contractApproved,
      locked: Boolean(blocker), lockedHint: blocker ? LOCK_HINTS[blocker] : null,
    });
  }
  // Planner read-only link never gets a Payments tab — the payments endpoint
  // won't serve their token, so exposing the tab would only surface an error.
  if (!d.readOnly && (contractApproved || d.hasPayments)) {
    const blocker = paymentStarted ? null : blockerFor('payment');
    docTabs.push({
      key: 'payments', label: 'Payments', icon: DollarSign,
      // Same defense-in-depth rule as the "What Happens Next" stepper: tick
      // only when BOTH paidInFull is true AND the balance is zero.
      approved: Boolean(d.paidInFull) && (Number(d.paymentBalance ?? 0) <= 0.005),
      locked: Boolean(blocker), lockedHint: blocker ? LOCK_HINTS[blocker] : null,
    });
  }
  // Skip is honoured even in preview: "Preview as client" exists to show what
  // the client sees, so a skipped module must be absent there too.
  // Planning is LOCKED until the stage before it is settled — the tab stays
  // visible but is not openable, and the form never mounts, until then.
  if (planningInPlay) {
    const blocker = planningDone ? null : blockerFor('planning');
    docTabs.push({
      key: 'planning', label: 'Planning', icon: ClipboardList,
      approved: d.planningSubmitted,
      locked: Boolean(blocker),
      lockedHint: blocker ? LOCK_HINTS[blocker] : null,
    });
  }
  return {
    previewMode, proposalApproved, proposalChangesRequested,
    contractApproved, planningDone, proposalAvailable,
    proposalInPlay, contractInPlay, contractSettled, docsPending,
    planningInPlay, retainerPaid, docTabs,
  };
}

/* The requested document must ALWAYS be one this client can reach. Anything
   else — approved proposal, unsent contract, stale deep link — falls to the
   FIRST document in the list. 'none' only when the list is genuinely empty. */
export function resolveActiveDoc(docTabs, requestedDoc) {
  // A locked tab (e.g. Planning before the retainer is paid) is visible but
  // never the ACTIVE document, so a stale deep link (?tab=planning) falls to
  // the client's first reachable step instead of flashing the locked pane.
  const selectable = (docTabs || []).filter((t) => !t.locked);
  const keys = selectable.map((t) => t.key);
  if (keys.includes(requestedDoc)) return requestedDoc;
  return keys[0] || 'none';
}

/* The strip may only hide itself when there is exactly ONE document and the
   client is already on it. Stranding a client is never an acceptable outcome
   of a tidiness rule. */
export function docTabsVisibleFor(docTabs, activeDoc) {
  const list = docTabs || [];
  if (list.length === 0) return false;
  if (list.length === 1 && list[0].key === activeDoc) return false;
  return true;
}

/* Copy that names a tab is generated from the same list, so it is impossible
   to print "open the Contract tab" while no Contract tab is rendered. */
export function tabPhrase(key, label, docTabs, activeDoc, fallback) {
  const rendered = docTabsVisibleFor(docTabs, activeDoc)
    && (docTabs || []).some((t) => t.key === key);
  return rendered ? `open the ${label} tab below` : fallback;
}


export default function ClientViewPage() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // 'network' = we could not reach/serve the portal (our fault, retryable)
  // 'invalid' = the server answered and said this token is not valid
  const [errorKind, setErrorKind] = useState(null);
  const [requestedDoc, setRequestedDoc] = useState('proposal');
  // If the admin skipped the proposal, the portal must open on the contract.
  // Runs once, the first time data lands, so it never fights the client's
  // own tab choices afterwards.
  const docDefaultedRef = useRef(false);
  // Flips true once the initial tab has been resolved (from ?tab= or defaults),
  // so the URL-sync effect never writes before the deep-link is read on load.
  const [tabRestored, setTabRestored] = useState(false);
  useEffect(() => {
    if (docDefaultedRef.current || !data) return;
    docDefaultedRef.current = true;
    // Deep-link support: `?tab=payments` (from admin "Send payment request"
    // emails) or any other supported tab key jumps straight there. Only wins
    // if the tab is actually available for this event, so a stale/misfired
    // link never traps the client on a blank pane. On refresh, the tab the
    // client was viewing is written back into `?tab=` (see the sync effect),
    // so this same path reopens exactly where they were.
    try {
      const paramTab = new URLSearchParams(window.location.search).get('tab');
      if (paramTab === 'payments' && !data?.readOnly && (Boolean(data?.hasPayments) || data?.paidInFull != null)) {
        setRequestedDoc('payments');
      } else if (paramTab && ['proposal', 'contract', 'planning'].includes(paramTab)) {
        setRequestedDoc(paramTab);
      }
    } catch { /* URL parse edge — fall through to default logic */ }
    setTabRestored(true);
  }, [data]);

  // Keep the web address in step with the tab the client is viewing, so a
  // browser refresh reopens the same tab. Uses replaceState (no extra
  // back-button history, no reload/flicker) and preserves every other param
  // (preview, fillIn, …). Waits for the initial tab to be resolved first.
  // NOTE: declared here at the TOP with every other hook — ABOVE all early
  // returns — and recomputes the active tab from `data` internally, so the
  // hook order can never change between renders (React #310 guard).
  useEffect(() => {
    if (!data || !tabRestored) return;
    try {
      const { docTabs } = clientDocAccess(data);
      const currentDoc = resolveActiveDoc(docTabs, requestedDoc);
      const params = new URLSearchParams(window.location.search);
      if (params.get('tab') === currentDoc) return;
      params.set('tab', currentDoc);
      const qs = params.toString();
      window.history.replaceState(window.history.state, '', `${window.location.pathname}${qs ? `?${qs}` : ''}`);
    } catch { /* URL edge — skip, in-memory tab still works */ }
  }, [data, tabRestored, requestedDoc]);

  // Proposal approval
  const [approvingProposal, setApprovingProposal] = useState(false);

  // Contract signature
  const [agreedTerms, setAgreedTerms] = useState(false);
  const [signerName, setSignerName] = useState('');
  const [signatureImage, setSignatureImage] = useState('');
  const [signatureMethod, setSignatureMethod] = useState('type');
  const [approvingContract, setApprovingContract] = useState(false);
  const [contractError, setContractError] = useState(null);
  // Server-confirmed total for signing. Set only when a re-read finds a
  // different figure, so the client sees the corrected number before signing.
  const [signingTotalOverride, setSigningTotalOverride] = useState(null);

  const fetchData = async (attempt = 0) => {
    try {
      const _preview = new URLSearchParams(window.location.search).get('preview') === '1';
      const res = await fetch(`${API}/api/leads/client-view/${token}${_preview ? '?preview=1' : ''}`);
      // 5xx = OUR outage. Never tell a client their link died because our
      // server did. Retry twice with backoff first — a transient 520 should
      // be invisible to them.
      if (res.status >= 500) {
        if (attempt < 2) {
          await new Promise((r) => setTimeout(r, 1200 * (attempt + 1)));
          return fetchData(attempt + 1);
        }
        setErrorKind('network');
        setError('unreachable');
        return;
      }
      // The server answered and rejected the token. This is the ONLY case
      // allowed to tell the client the link is not valid.
      if (!res.ok) {
        setErrorKind('invalid');
        setError('rejected');
        return;
      }
      const d = await res.json();
      setErrorKind(null);
      setError(null);
      setData(d);
    } catch (err) {
      // fetch() threw: offline, DNS, TLS, CORS. It never reached the server,
      // so we know NOTHING about whether the token is valid.
      if (attempt < 2) {
        await new Promise((r) => setTimeout(r, 1200 * (attempt + 1)));
        return fetchData(attempt + 1);
      }
      setErrorKind('network');
      setError('unreachable');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (token) fetchData(); }, [token]);

  /**
   * Dynamically brand the browser tab with the tenant's name + logo so the
   * client experience feels like "my vendor's website" all the way into the
   * browser chrome. Restores the app defaults on unmount so navigating back
   * to the main app doesn't leak tenant branding.
   */
  useEffect(() => {
    if (!data) return undefined;
    const company = data.companyName || 'Event Portal';
    const clientName = data.clientName || '';
    const prevTitle = document.title;
    document.title = clientName ? `${clientName} — ${company}` : company;

    let faviconLink = null;
    let prevFaviconHref = null;
    if (data.companyLogo) {
      const resolved = /^(https?:|data:)/.test(data.companyLogo)
        ? data.companyLogo
        : `${API}${data.companyLogo}`;
      // Find or create the <link rel="icon"> element.
      faviconLink = document.querySelector("link[rel~='icon']");
      if (!faviconLink) {
        faviconLink = document.createElement('link');
        faviconLink.rel = 'icon';
        document.head.appendChild(faviconLink);
      } else {
        prevFaviconHref = faviconLink.href;
      }
      faviconLink.href = resolved;
    }

    return () => {
      document.title = prevTitle;
      if (faviconLink && prevFaviconHref !== null) {
        faviconLink.href = prevFaviconHref;
      }
    };
  }, [data]);

  /* Per-link social/link-preview meta (Open Graph / Twitter), resolved from
   * the token by the backend so the SPA head and the crawler HTML share ONE
   * source. Applied only on this client-view route and fully restored on
   * unmount, so the STAFFED marketing meta on every other route is untouched.
   * (Raw non-JS unfurlers only see this when Emergent's managed crawler
   * pre-render layer executes the SPA — see the handoff notes.) */
  useEffect(() => {
    if (!token) return undefined;
    const created = [];
    const prevContent = new Map();
    const prevTitle = document.title;
    let cancelled = false;
    const upsert = (selector, attr, key, value) => {
      let el = document.head.querySelector(selector);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, key);
        document.head.appendChild(el);
        created.push(el);
      } else if (!prevContent.has(el)) {
        prevContent.set(el, el.getAttribute('content'));
      }
      el.setAttribute('content', value);
    };
    (async () => {
      try {
        const res = await fetch(`${API}/api/og/client-link/${token}/meta`);
        const m = await res.json();
        if (cancelled || !m || !m.found) return;
        document.title = m.title;
        upsert("meta[property='og:title']", 'property', 'og:title', m.title);
        upsert("meta[property='og:site_name']", 'property', 'og:site_name', m.site_name);
        upsert("meta[property='og:description']", 'property', 'og:description', m.description);
        upsert("meta[property='og:url']", 'property', 'og:url', m.url);
        upsert("meta[property='og:image']", 'property', 'og:image', m.image);
        upsert("meta[property='og:image:secure_url']", 'property', 'og:image:secure_url', m.image);
        upsert("meta[name='twitter:card']", 'name', 'twitter:card', 'summary_large_image');
        upsert("meta[name='twitter:title']", 'name', 'twitter:title', m.title);
        upsert("meta[name='twitter:description']", 'name', 'twitter:description', m.description);
        upsert("meta[name='twitter:image']", 'name', 'twitter:image', m.image);
        upsert("meta[name='description']", 'name', 'description', m.description);
      } catch (e) {
        /* link-preview meta is best-effort; never block the portal */
      }
    })();
    return () => {
      cancelled = true;
      document.title = prevTitle;
      prevContent.forEach((val, el) => {
        if (val === null) el.removeAttribute('content');
        else el.setAttribute('content', val);
      });
      created.forEach((el) => el.remove());
    };
  }, [token]);


  const [changesOpen, setChangesOpen] = useState(false);
  // Review step — the enhancements bar opens it; it carries the SAME approve /
  // request-changes actions the proposal has always used.
  const [reviewOpen, setReviewOpen] = useState(false);
  const [planningTab, setPlanningTab] = useState('General');
  // Typed signature → the same data URL shape the signature pad produces, so
  // the existing sign endpoint is reused untouched.
  const typedSignatureImage = (name) => {
    const txt = String(name || '').trim();
    if (!txt) return '';
    const c = document.createElement('canvas');
    c.width = 600; c.height = 160;
    const ctx = c.getContext('2d');
    ctx.fillStyle = '#FBF8F1'; ctx.fillRect(0, 0, c.width, c.height);
    ctx.fillStyle = '#141414';
    ctx.font = '72px "Dancing Script", cursive';
    ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
    ctx.fillText(txt, c.width / 2, c.height / 2);
    return c.toDataURL('image/png');
  };
  const [selectedAddOns, setSelectedAddOns] = useState([]);
  const [termsOpen, setTermsOpen] = useState(false);
  const [changesNote, setChangesNote] = useState('');
  const [requestingChanges, setRequestingChanges] = useState(false);
  const handleRequestChanges = async () => {
    const note = changesNote.trim();
    if (!note) return;
    setRequestingChanges(true);
    try {
      const res = await fetch(`${API}/api/leads/client-view/${token}/request-proposal-changes`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ note }),
      });
      if (res.ok) {
        setData(prev => ({ ...prev, proposalClientStatus: 'changes_requested' }));
        setChangesOpen(false);
        setChangesNote('');
        toast.success("Thanks — we'll review your notes and follow up shortly.");
      } else {
        toast.error('Could not send your request. Please try again.');
      }
    } catch {
      toast.error('Could not send your request. Please try again.');
    } finally {
      setRequestingChanges(false);
    }
  };
  const handleApproveProposal = async () => {
    setApprovingProposal(true);
    try {
      const res = await fetch(`${API}/api/leads/client-view/${token}/approve-proposal`, { method: 'PATCH' });
      if (res.ok) {
        setData(prev => ({ ...prev, proposalClientStatus: 'approved', proposalApprovedAt: new Date().toISOString() }));
      }
    } finally {
      setApprovingProposal(false);
    }
  };

  // ONE contract document: the server-rendered copy, printed in a hidden
  // iframe. Every print control on this page calls this, so what the client
  // prints is exactly what they download and what we email.
  const printContractDocument = () => {
    if (document.getElementById('contract-print-frame')) return;
    const iframe = document.createElement('iframe');
    iframe.id = 'contract-print-frame';
    Object.assign(iframe.style, {
      position: 'fixed', right: '0', bottom: '0',
      width: '0', height: '0', border: '0',
    });
    iframe.src = `${API}/api/documents/contracts/by-token/${token}/html`;
    let printed = false;
    iframe.onload = () => {
      if (printed) return;
      printed = true;
      try {
        iframe.contentWindow.focus();
        iframe.contentWindow.print();
      } catch { /* cross-origin edge, fall through */ }
      setTimeout(() => iframe.remove(), 1500);
    };
    document.body.appendChild(iframe);
  };

  const handleApproveContract = async () => {
    setContractError(null);
    if (!agreedTerms) { setContractError('Please agree to the contract terms.'); return; }
    if (!signerName.trim()) { setContractError('Please provide your signature.'); return; }
    if (!signatureImage) { setContractError('Please provide your signature.'); return; }
    // ── The client signs the number the server calculated ──
    // The figure on screen and the figure recorded must come from the same
    // read. Re-read the total immediately before recording: if it moved
    // (an add-on landed, the owner repriced), show the client the corrected
    // number and record NOTHING until they have seen it.
    if (!signingTotalResolved) {
      setContractError("We can't confirm your total right now — please refresh before signing. Nothing has been recorded.");
      return;
    }
    setApprovingContract(true);
    try {
      let confirmed = signingTotal;
      try {
        const fresh = await fetch(`${API}/api/leads/client-view/${token}`);
        if (fresh.ok) {
          const fd = await fresh.json();
          const fc = fd.contract || {};
          confirmed = fc.packageNewTotal != null ? fc.packageNewTotal : fc.contractAmount;
        }
      } catch { confirmed = signingTotal; }
      if (confirmed == null) {
        setContractError("We can't confirm your total right now — please refresh before signing. Nothing has been recorded.");
        setApprovingContract(false);
        return;
      }
      if (Math.abs(Number(confirmed) - Number(signingTotal)) > 0.005) {
        setSigningTotalOverride(Number(confirmed));
        setContractError(
          `Your total has been updated to ${formatMoney(confirmed)}. Please review the new figure and sign again — nothing has been recorded.`,
        );
        setApprovingContract(false);
        return;
      }
      const res = await fetch(`${API}/api/leads/client-view/${token}/approve-contract`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          signerName: signerName.trim(),
          signatureImage,
          signatureMethod,
        }),
      });
      if (res.ok) {
        const d = await res.json();
        setData(prev => ({
          ...prev,
          contractClientStatus: 'approved',
          contractApprovedAt: d.contractApprovedAt,
          signerName: d.signerName,
          signedAt: d.contractApprovedAt,
          signatureImage,
          signatureMethod,
        }));

        // ── Luxury gold-particle celebration ──
        try {
          const goldPalette = ['#D4AF37', '#F4DC7A', '#B8872A', '#FFF3B8'];
          const end = Date.now() + 1400;
          // Two symmetric side-bursts for elegance
          const frame = () => {
            confetti({
              particleCount: 3,
              angle: 60,
              spread: 55,
              startVelocity: 45,
              origin: { x: 0, y: 0.75 },
              colors: goldPalette,
              scalar: 0.9,
              ticks: 80,
              gravity: 0.9,
              drift: 0.2,
              shapes: ['circle', 'square'],
            });
            confetti({
              particleCount: 3,
              angle: 120,
              spread: 55,
              startVelocity: 45,
              origin: { x: 1, y: 0.75 },
              colors: goldPalette,
              scalar: 0.9,
              ticks: 80,
              gravity: 0.9,
              drift: -0.2,
              shapes: ['circle', 'square'],
            });
            if (Date.now() < end) requestAnimationFrame(frame);
          };
          frame();
          // Single soft center-burst at t=0 for the "hero moment"
          confetti({
            particleCount: 40,
            spread: 80,
            startVelocity: 28,
            origin: { x: 0.5, y: 0.55 },
            colors: goldPalette,
            scalar: 1.1,
            ticks: 110,
            gravity: 0.7,
            shapes: ['circle'],
          });
        } catch {
          /* non-fatal — signing succeeded */
        }
      }
    } finally {
      setApprovingContract(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#080A14' }}>
        <div className="flex items-center gap-3 text-white/30">
          <Loader2 className="w-5 h-5 animate-spin" />
          <span className="text-sm">Loading your documents...</span>
        </div>
      </div>
    );
  }

  // Our outage. Calm, honest, retryable, explicitly NOT the client's fault.
  // Luxury tone — no red, no alarm.
  if (errorKind === 'network') {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center px-4" style={{ background: '#080A14' }} data-testid="client-portal-unreachable">
        <div
          className="w-14 h-14 rounded-2xl flex items-center justify-center mb-4"
          style={{ background: 'rgba(212,175,55,0.08)', border: '1px solid rgba(212,175,55,0.15)' }}
        >
          <Loader2 className="w-7 h-7 text-[#D4AF37]/50" />
        </div>
        <p className="text-[16px] text-white/60 font-medium mb-1.5">We can&apos;t load your portal right now</p>
        <p className="text-[13px] text-white/35 text-center max-w-xs leading-relaxed mb-5">This is on our end, not your link. It usually clears within a minute or two.</p>
        <button
          type="button"
          onClick={() => { setError(null); setErrorKind(null); setLoading(true); fetchData(0); }}
          className="px-5 py-2 rounded-lg text-[13px] font-medium transition-colors"
          style={{ background: 'rgba(212,175,55,0.12)', border: '1px solid rgba(212,175,55,0.25)', color: '#D4AF37' }}
        >
          Try again
        </button>
      </div>
    );
  }

  if (errorKind === 'invalid') {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center px-4" style={{ background: '#080A14' }}>
        <div
          className="w-14 h-14 rounded-2xl flex items-center justify-center mb-4"
          style={{ background: 'rgba(212,175,55,0.08)', border: '1px solid rgba(212,175,55,0.15)' }}
        >
          <FileText className="w-7 h-7 text-[#D4AF37]/50" />
        </div>
        <p className="text-[16px] text-white/60 font-medium mb-1.5">This link isn&apos;t active</p>
        <p className="text-[13px] text-white/35 text-center max-w-xs leading-relaxed">This link may have expired, or the event it points to is no longer available. Please reach out to the person who sent it and they&apos;ll be happy to share an updated link.</p>
      </div>
    );
  }

  // Loaded with neither data nor a classified error: we do not know what
  // happened, so we do not get to assert the link is dead.
  if (!data) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center px-4" style={{ background: '#080A14' }} data-testid="client-portal-unknown">
        <p className="text-[16px] text-white/60 font-medium mb-1.5">We can&apos;t load your portal right now</p>
        <p className="text-[13px] text-white/35 text-center max-w-xs leading-relaxed">Please refresh, or reach out to your event team if it keeps happening.</p>
      </div>
    );
  }

  // ── Post-event portal cutoff ─────────────────────────────────────
  // Backend short-circuits to `{ expired: true, ... }` once the event
  // is more than CLIENT_PORTAL_EXPIRY_DAYS in the past. We render a
  // branded "this event has wrapped" screen instead of the full portal
  // so the client sees a polite goodbye, not a sterile 404.
  if (data?.expired) {
    const resolvedLogo = data.companyLogo
      ? (/^(https?:|data:)/.test(data.companyLogo) ? data.companyLogo : `${API}${data.companyLogo}`)
      : null;
    const company = data.companyName || 'Your Event Team';
    return (
      <div
        className="min-h-screen flex flex-col items-center justify-center px-6 py-12 text-center"
        style={{ background: 'radial-gradient(circle at top, rgba(212,175,55,0.08), transparent 28%), linear-gradient(180deg, #02040B 0%, #030714 45%, #02040B 100%)' }}
        data-testid="client-portal-expired"
      >
        {resolvedLogo && (
          <img
            src={resolvedLogo}
            alt={company}
            className="w-16 h-16 rounded-2xl object-contain mb-6 ring-1 ring-white/10"
            onError={(e) => { e.currentTarget.style.display = 'none'; }}
          />
        )}
        <p className="text-[11px] uppercase tracking-[0.18em] text-[#D4AF37]/70 mb-3">
          {company}
        </p>
        <h1 className="text-2xl sm:text-3xl font-semibold text-white mb-3 tracking-tight">
          Your event has wrapped
        </h1>
        <p className="text-[14px] text-white/55 max-w-md leading-relaxed">
          {data.message || 'Thanks for working with us! Your event details are no longer available here.'}
        </p>
        <p className="text-[12px] text-white/25 mt-6">
          Need anything? Reach out to your event team directly.
        </p>
      </div>
    );
  }

  // Modules the admin skipped upstream. A skipped step is dropped from the
  // portal completely - no tab, no rail node, no mention in the copy.
  // ── ONE list, built ABOVE the router ─────────────────────────────────
  // Availability and the approval states are derived in clientDocAccess (top
  // of this file) so the tab strip, the default document, the redirect gate,
  // the step copy and the tab-naming copy all read the same answer. The
  // approval flags used to be declared BELOW `activeDoc`; they are hoisted
  // into that helper so ordering cannot strand a client again.
  const {
    previewMode,
    proposalApproved, proposalChangesRequested,
    contractApproved, planningDone, proposalInPlay, contractInPlay,
    contractSettled, docsPending,
    planningInPlay, retainerPaid, docTabs,
  } = clientDocAccess(data);
  const skippedStepKeys = skippedModuleKeys(data);
  if (!proposalInPlay && !skippedStepKeys.includes('proposal')) skippedStepKeys.push('proposal');
  // `activeDoc` is read at 18 render sites. Gating each one would drift the
  // moment a 19th is added, so the gate lives at the source instead: the raw
  // choice is `requestedDoc`, and `activeDoc` is whatever survives being
  // checked against the one list. It can never be a document absent from it.
  const activeDoc = resolveActiveDoc(docTabs, requestedDoc);  const docTabsVisible = docTabsVisibleFor(docTabs, activeDoc);
  // ── The number the client signs ──
  // Comes from the BACKEND: `packageNewTotal` already includes any add-ons the
  // client picked, computed server-side. The browser adds nothing up. When it
  // cannot be resolved, signing is blocked rather than run against a guess.
  const signingTotal = signingTotalOverride != null
    ? signingTotalOverride
    : (data.contract && data.contract.packageNewTotal != null
        ? data.contract.packageNewTotal
        : (data.contract && data.contract.contractAmount != null
            ? data.contract.contractAmount
            // The base price lives on the EVENT (= the approved proposal total).
            // Contracts whose own contractAmount never landed used to dead-end
            // the client on "we can't confirm your total"; the base is sourced
            // here instead so signing proceeds on the real number.
            : (data.event && data.event.contractAmount != null
                ? data.event.contractAmount
                : (data.proposal && data.proposal.totalAmount != null ? data.proposal.totalAmount : null))));
  const signingTotalResolved = signingTotal != null && Number(signingTotal) > 0;
  // `docData` is read here rather than further down so the ONE section list can
  // carry the ONE heading for each section. Every render site below reads
  // sec.label, so resolving it once here is what keeps the admin screen and the
  // client screen on the same name. Headings always come from the PROPOSAL
  // record, so the contract's services section is named the same as the
  // proposal's rather than becoming a fourth name for it.
  const docData = activeDoc === 'proposal' ? data.proposal : (activeDoc === 'contract' ? data.contract : null);
  // Mobile proposal mock owns the header/hero/steps at narrow widths.
  // Mobile screens own the header / hero / stepper / primary action for the
  // four client documents at narrow widths.
  const cvmOn = ['proposal', 'contract', 'payments', 'planning'].includes(activeDoc)
    && !data.readOnly
    && (activeDoc === 'payments' || activeDoc === 'planning'
        ? true
        : (!!docData && !(activeDoc === 'proposal' && (proposalApproved || proposalChangesRequested))
           && !(activeDoc === 'contract' && contractApproved)));
  const _headingSource = data.proposal || docData;
  // Review step figures — frozen base investment + the selected enhancements.
  // Derived from the live selection on every render, so a tap changes the
  // number in the same interaction. The backend stays the source of truth and
  // still records the selection exactly as before.
  const reviewBase = Number((docData && (docData.totalAmount ?? docData.contractAmount)) ?? 0) || 0;
  const selectedAddOnsTotal = selectedAddOns.reduce((sum, a) => sum + (Number(a.amount) || 0), 0);
  const reviewGrandTotal = reviewBase + selectedAddOnsTotal;

  const sections = (activeDoc === 'proposal' ? PROPOSAL_SECTIONS : CONTRACT_SECTIONS)
    .map((s) => ({ ...s, label: headingFor(_headingSource, s.headingKey, s.label) }))
    // ONE package list on the proposal, under ONE name. The hero used to
    // repeat the same items as chips above this section.
    .map((s) => (activeDoc === 'proposal' && s.key === 'servicesIncluded'
      ? { ...s, label: "What's included" }
      : s))
    // The designed-experience and closing-statement paragraphs said the same
    // thing the overview already says, so the client proposal keeps the
    // overview, the package list and the investment figure only.
    .filter((s) => !(activeDoc === 'proposal'
      && ['suggestedExperience', 'closingStatement'].includes(s.key)));

  // Dynamic header and message based on client progress
  const isPortalPhase = contractApproved;
  // Name the document ONLY when one actually exists. `docData` is the same
  // value the pane below uses to decide whether to render a document, so the
  // eyebrow and the body can never disagree — one signal, two consumers.
  const headerLabel = isPortalPhase ? 'YOUR EVENT PORTAL'
    : activeDoc === 'payments' ? 'YOUR PAYMENT'
    : activeDoc === 'planning' ? 'YOUR PLANNING DETAILS'
    : (activeDoc === 'proposal' && docData) ? 'YOUR PROPOSAL'
    : (activeDoc === 'contract' && docData) ? 'YOUR CONTRACT'
    : 'YOUR EVENT';

  // White-label brand — always the tenant's own company, resolved server-side
  // in the client-view payload (data.companyName). Never hardcode a company
  // here; a neutral fallback is used only if a tenant has set no name at all.
  const brandName = (data.companyName || '').trim() || 'Your Event Team';

  // ── ONE derivation of where this client stands ───────────────────────
  // ROOT CAUSE this replaces: three parallel ladders (getDynamicMessage,
  // updateHeadline, nextStepPill) each re-derived the same conditions from
  // scratch, and none of them knew about money. A client with an unpaid
  // balance fell through every document branch to a default that told them
  // "Nothing needed from you right now" while a payment was due that day —
  // a failure rendered as good news. One table now answers all three
  // questions, and money is a state the table can see.
  const clientStage = (() => {
    const name = (data.clientName || '').split(' ')[0] || 'there';
    const balanceDue = Number(data.paymentBalance ?? 0);
    const moneyOwed = Boolean(data.hasPayments) && !data.paidInFull && balanceDue > 0.005;
    const owed = formatMoney(balanceDue);
    const settled = contractSettled && (planningDone || !planningInPlay)
      && Boolean(data.paidInFull) && balanceDue <= 0.005;
    // Did they come here to pay? Either the emailed link said so
    // (?tab=payments) or they opened Payments themselves. Declared intent
    // beats funnel order — lead them to the one thing they came to do.
    let cameToPay = activeDoc === 'payments';
    try {
      if (new URLSearchParams(window.location.search).get('tab') === 'payments') cameToPay = true;
    } catch { /* URL parse edge — fall back to activeDoc */ }
    const sign = (body) => `Hi ${name},\n\n${body}\n\nBest,\n${brandName}`;
    // ── S20 — the 'settled' shape, built ONCE from the steps that actually
    // ran on THIS event. Two different conditions reach it (nothing left to
    // do; nothing ever in play), so both branches call this one helper — the
    // copy can no longer drift or assert a step that never happened.
    const buildSettled = () => {
      const daysUntil = getDaysUntilEvent(data.event?.eventDate);
      const past = daysUntil !== null && daysUntil < 0;
      const paymentReceived = Boolean(data.hasPayments) && Boolean(data.paidInFull) && balanceDue <= 0.005;

      // Headline follows the same day count as the closing line.
      let headline = `You're All Set`;
      if (past) headline = 'Thank You';
      else if (daysUntil === 0) headline = `Today's the Day`;
      else if (daysUntil === 1 || daysUntil === 2) headline = `Everything's Ready`;

      // A past event gets the short thank-you only — the review request owns
      // everything after the event; this card must not compete with it.
      if (past) {
        return {
          key: 'settled', pill: null, next: 'done', headline,
          message: sign('Thank you for having us — it was a pleasure.'),
        };
      }

      // First paragraph — name ONLY the steps whose signal is true.
      const phrases = [];
      if (proposalApproved) phrases.push('proposal approved');
      if (contractApproved) phrases.push('contract signed');
      if (planningDone) phrases.push('planning details in');
      if (paymentReceived) phrases.push('payment received');
      let joined;
      if (phrases.length >= 3) joined = phrases.join(', ');
      else if (phrases.length === 2) joined = phrases.join(' and ');
      else joined = phrases[0] || '';
      const firstPara = phrases.length
        ? `Every step is done — ${joined}. Your event is fully booked with ${brandName}.`
        : 'Everything is confirmed on your event.';

      // Closing line — follows the calendar. Missing/unparseable date → 60+.
      const to12h = (t) => {
        const mm = /^(\d{1,2}):(\d{2})/.exec(t || '');
        if (!mm) return '';
        let h = parseInt(mm[1], 10);
        const ap = h >= 12 ? 'PM' : 'AM';
        h = h % 12 || 12;
        return `${h}:${mm[2]} ${ap}`;
      };
      const startTime = to12h(data.event?.startTime);
      const weekday = formatWeekday(data.event?.eventDate, { long: true });
      const d = daysUntil;
      let closing;
      if (d === null || d >= 60) closing = "Nothing needed from you right now — we'll be in touch as your date gets closer.";
      else if (d >= 15) closing = "Nothing needed from you. We'll check in a couple of weeks before your date.";
      else if (d >= 8) closing = "Nothing needed from you. We'll be in touch next week to confirm the final details.";
      else if (d >= 3) closing = "Nothing needed from you. We'll be in touch this week to confirm the final details.";
      else if (d === 2) closing = `Nothing needed from you — we're set on our end. See you ${weekday}.`;
      else if (d === 1) closing = 'Nothing needed from you — everything is set for tomorrow. See you then.';
      else closing = startTime ? `Everything is set on our end. See you at ${startTime}.` : 'Everything is set on our end. See you soon.';

      return {
        key: 'settled', pill: null, next: 'done', headline,
        message: sign(`${firstPara}\n\n${closing}`),
      };
    };
    // Every pointer below is generated from the ONE document list, so no line
    // can tell the client to open a tab that was never drawn.
    const paymentsPointer = tabPhrase('payments', 'Payments', docTabs, activeDoc, 'the payment details are below');
    const contractPointer = tabPhrase('contract', 'Contract', docTabs, activeDoc, 'your contract is below');
    const planningPointer = tabPhrase('planning', 'Planning', docTabs, activeDoc, 'the planning details are below');
    // ONE source for "what happens after you sign". It walks the REAL step
    // order (Proposal → Contract → Payment → Planning) and names the first
    // step that is still in play, so this copy can never skip the retainer
    // again — and a future reorder of the flow stays true automatically.
    const postContractFlow = [
      { key: 'payment', inPlay: !retainerPaid, line: 'It should also reflect your retainer to lock in your date.' },
      { key: 'planning', inPlay: planningInPlay, line: "Once it's signed, we'll move on to your planning details." },
    ];
    const afterSigningLine = () => {
      const remaining = postContractFlow.filter((st) => st.inPlay);
      if (!remaining.length) return '';
      const first = remaining[0];
      let out = first.line;
      if (first.key === 'payment' && remaining.some((st) => st.key === 'planning')) {
        out += ' Planning opens up as soon as your retainer is in.';
      }
      return out;
    };
    const tabsHint = docTabsVisible
      ? '\n\nYou can review your documents anytime using the tabs below.'
      : '';
    // S35 — `idle` and `alsoOwed` are built from data.nextPayment below (next
    // to buildMoneyShape), so the secondary money sentence follows the same due
    // state as everything else instead of always saying "still due".
    // BLOCK S29 — ONE money headline builder, driven by the server's
    // nextPayment.state (upcoming / due / overdue). Both money branches call
    // it. Falls back to today's exact strings when nextPayment is missing
    // (old cached payload). Dates are parsed from the yyyy-mm-dd PARTS — never
    // new Date(iso), which shifts a day in US timezones.
    const _MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];
    const fmtDueDate = (iso) => {
      if (!iso || typeof iso !== 'string') return { full: '', short: '' };
      const parts = iso.split('-');
      const y = Number(parts[0]); const m = Number(parts[1]); const d = Number(parts[2]);
      if (!y || !m || !d || m < 1 || m > 12) return { full: '', short: '' };
      const name = _MONTHS[m - 1];
      return { full: `${name} ${d}, ${y}`, short: `${name} ${d}` };
    };
    const buildMoneyShape = (nextPayment, owedStr, key) => {
      const next = key === 'paying' ? 'payment' : null;
      if (!nextPayment || !nextPayment.state) {
        // FALLBACK — today's exact strings for this branch.
        return {
          key,
          headline: 'Your Payment',
          pill: key === 'paying' ? `Due: ${owedStr}` : null,
          next,
          message: sign(`${owedStr} is still due on your event. You can pay by card or Zelle in the payment details below.\n\nLet us know if you have any questions.`),
        };
      }
      const amt = formatMoney(Number(nextPayment.amount) || 0);
      const { full: dFull, short: dShort } = fmtDueDate(nextPayment.dueDate);
      // System-generated reminder wording follows the one full-payment rule:
      // a full payment reads "full payment", not the line's "retainer" label.
      // (A message the owner typed by hand is `data.message`, untouched.)
      const label = data.isFullPayment ? 'full payment' : ((nextPayment.label || '').trim().toLowerCase() || 'payment');
      if (nextPayment.state === 'upcoming') {
        return {
          key,
          headline: "You're all set for now",
          pill: `Next: ${amt} · ${dShort}`,
          next,
          message: sign(`Your date is held. Your next payment of ${amt} is due ${dFull}. Nothing is needed until then.`),
        };
      }
      if (nextPayment.state === 'overdue') {
        return {
          key,
          headline: `Your ${label} is past due`,
          pill: `Past due: ${amt}`,
          next,
          message: sign(`${amt} was due ${dFull}. Please take care of it when you can — card or Zelle below. Questions? Just reach out.`),
        };
      }
      // due (default)
      return {
        key,
        headline: dShort ? `Your ${label} is due ${dShort}` : `Your ${label} is due`,
        pill: `Due: ${amt}`,
        next,
        message: sign(dFull
          ? `${amt} is due ${dFull}. You can pay by card or Zelle in the payment details below.`
          : `${amt} is due. You can pay by card or Zelle in the payment details below.`),
      };
    };
    // S35 — the SECONDARY money sentences read the SAME due state as
    // buildMoneyShape (data.nextPayment), reusing its amount/date formatting, so
    // a client whose next payment is months away is never told it is "still
    // due". Falls back to today's exact strings when nextPayment is missing.
    const buildOwedSentences = (nextPayment) => {
      if (!moneyOwed) return { idle: 'Nothing needed from you right now.', alsoOwed: '' };
      if (!nextPayment || !nextPayment.state) {
        return {
          idle: `${owed} is still due — ${paymentsPointer} whenever you're ready.`,
          alsoOwed: `\n\nSeparately, ${owed} is still due — ${paymentsPointer}.`,
        };
      }
      const amt = formatMoney(Number(nextPayment.amount) || 0);
      const { full: dFull } = fmtDueDate(nextPayment.dueDate);
      if (nextPayment.state === 'upcoming') {
        const line = `Your next payment of ${amt} is due ${dFull} — nothing is needed until then.`;
        return { idle: line, alsoOwed: `\n\n${line}` };
      }
      if (nextPayment.state === 'overdue') {
        return {
          idle: dFull ? `${amt} was due ${dFull} — ${paymentsPointer}.` : `${amt} was due — ${paymentsPointer}.`,
          alsoOwed: dFull ? `\n\nSeparately, ${amt} was due ${dFull} — ${paymentsPointer}.` : `\n\nSeparately, ${amt} was due — ${paymentsPointer}.`,
        };
      }
      // due (default)
      return {
        idle: dFull ? `${amt} is due ${dFull} — ${paymentsPointer}.` : `${amt} is due — ${paymentsPointer}.`,
        alsoOwed: dFull ? `\n\nSeparately, ${amt} is due ${dFull} — ${paymentsPointer}.` : `\n\nSeparately, ${amt} is due — ${paymentsPointer}.`,
      };
    };
    const { idle, alsoOwed } = buildOwedSentences(data.nextPayment);
    let s;
    if (moneyOwed && cameToPay) {
      s = buildMoneyShape(data.nextPayment, owed, 'paying');
    } else if (settled) {
      s = buildSettled();
    } else if (contractSettled && (planningDone || !planningInPlay)) {
      s = {
        key: 'payment_last',
        headline: `You're Almost Locked In`,
        pill: 'Final Step: Payment',
        next: 'payment',
        // Never thank a client for planning details that were never asked
        // for: when planning is skipped this branch is reached without any
        // planning having happened.
        message: sign(`${planningDone ? 'Thank you for completing your planning details. The last step is' : 'Your contract is signed — thank you. The last step is'} your payment — ${paymentsPointer} to see what's due.${tabsHint}\n\nLet us know if you have any questions.`),
      };
    } else if (contractSettled && planningInPlay && retainerPaid) {
      s = {
        key: 'planning',
        headline: 'Planning Time',
        pill: 'Next: Planning Details',
        next: 'planning',
        message: sign(`Your contract is signed — thank you. Next, ${planningPointer} and fill in your event details so we can prepare everything.\n\nLet us know if you have any questions.${alsoOwed}`),
      };
    } else if (contractSettled && planningInPlay && !retainerPaid) {
      // Planning is gated behind the retainer: until the deposit lands the
      // next step is Payment, never the locked Planning tab.
      s = {
        key: 'retainer_before_planning',
        headline: 'Secure Your Date',
        pill: 'Next: Payment',
        next: 'payment',
        message: sign(`Your contract is signed — thank you. Next, ${paymentsPointer} to pay your retainer and lock in your date. Your planning details unlock right after.\n\nLet us know if you have any questions.`),
      };
    } else if (contractSettled) {
      s = {
        key: 'contract_signed',
        headline: 'Contract Signed',
        pill: null,
        next: null,     // waiting on US, not them
        message: sign(`Your contract is signed — thank you. We'll be in touch shortly with your next steps.\n\n${idle}`),
      };
    } else if (proposalApproved && contractInPlay) {
      s = {
        key: 'sign_contract',
        headline: 'Ready to Sign',
        pill: 'Next: Sign Contract',
        next: 'contract',
        message: sign(`${afterSigningLine()}\n\nLet us know if you have any questions.`),
      };
    } else if (proposalApproved) {
      s = {
        key: 'contract_pending',
        headline: 'Proposal Approved',
        pill: null,
        next: null,     // we are preparing it
        message: sign(`Thanks for approving your proposal. We're preparing your contract now — we'll email you the moment it's ready to sign.\n\n${idle}`),
      };
    } else if (!proposalInPlay && contractInPlay) {
      s = {
        key: 'contract_only',
        headline: 'Your Contract Is Ready',
        pill: 'Next: Sign Contract',
        next: 'contract',
        message: data.message || sign(`Your contract is ready for review — it's below. Once you've reviewed and signed it, you'll be able to move forward with the next steps directly from there.\n\nLet us know if you have any questions.${alsoOwed}`),
      };
    } else if (docsPending) {
      s = {
        key: 'preparing',
        headline: 'Getting Your Documents Ready',
        pill: null,
        next: null,     // nothing sent yet
        message: data.message || sign(`We're preparing your documents now — we'll email you as soon as they're ready.\n\n${idle}`),
      };
    } else if (proposalInPlay) {
      s = {
        key: 'review_proposal',
        headline: 'Your Proposal Is Ready',
        pill: 'Next: Approve Proposal',
        next: 'proposal',
        message: data.message || sign(`Your proposal is ready to look through — everything is below. When it looks right, approve it and we'll prepare your contract.\n\nIf you'd like anything changed, just tell us.${alsoOwed}`),
      };
    } else if (moneyOwed) {
      // No document step is in play and no earlier branch matched, but a
      // balance is owed: the existing 'preparing' shape (pill/next null)
      // carries the existing money branch's headline and message.
      s = buildMoneyShape(data.nextPayment, owed, 'preparing');
    } else {
      // Nothing in play, nothing owed — the same settled shape, built once.
      s = buildSettled();
    }
    // An admin's custom message (data.message) replaces our copy, but it must
    // never hide money. The pill is structural, so the balance surfaces
    // without editing someone else's words.
    if (moneyOwed && !s.pill) s.pill = `Due: ${owed}`;
    return s;
  })();
  const dynamicMessage = clientStage.message;
  const updateHeadline = clientStage.headline;
  const nextStepPill = clientStage.pill;
  // The pointer must never name a step that was turned off, so it is walked
  // past anything skipped (or already done) before it reaches the rail.
  const nextStepKey = resolveNextStep(clientStage.next ?? null, data, {
    proposal: proposalApproved,
    contract: contractApproved,
    payment: Boolean(data.paidInFull) && (Number(data.paymentBalance ?? 0) <= 0.005),
    planning: planningDone,
  });

  return (
    <div
      className={`min-h-screen cv-portal${activeDoc === 'contract' ? ' cvp-contract' : ''}`}
      style={{
        background: 'radial-gradient(circle at top, rgba(212,175,55,0.08), transparent 28%), linear-gradient(180deg, #02040B 0%, #030714 45%, #02040B 100%)',
      }}
      data-testid="client-view-page"
    >
      {/* Mobile legibility: raise the smallest label text to a readable floor
          on phones only (max-width 640px). Desktop is untouched. Scoped to
          .cv-portal so only the client portal is affected. */}
      <style>{`
        @media (max-width: 640px) {
          .cv-portal [class*="text-[9px]"]  { font-size: 11px !important; }
          .cv-portal [class*="text-[10px]"] { font-size: 12px !important; }
          .cv-portal [class*="text-[11px]"] { font-size: 12px !important; }
          .cv-portal [class*="text-[12px]"] { font-size: 13px !important; }
        }
      `}</style>
      {/* One contract document. The server-rendered PDF/HTML is it — for
          print, download and email, signed or not. The React print component
          that used to render here was a SECOND renderer answering the same
          question, which is how the missing total survived two fixes: the fix
          landed in the copy nobody actually received. Printing goes through
          the buttons below, which fetch the server document. */}

      {/* Header */}
      <header
        className={`sticky top-0 z-30 backdrop-blur-xl${cvmOn ? ' cvm-hide-mobile' : ''}`}
        style={{ background: 'rgba(2,4,11,0.82)', borderBottom: '1px solid rgba(212,175,55,0.12)' }}
      >
        <div className="max-w-[640px] mx-auto px-4 sm:px-6 py-5 sm:py-6">
          <div className="flex items-start gap-4 sm:gap-5">
            {data.companyLogo ? (
              <img
                src={/^(https?:|data:)/.test(data.companyLogo) ? data.companyLogo : `${API}${data.companyLogo}`}
                alt={data.companyName || 'Company logo'}
                className="h-11 w-11 sm:h-14 sm:w-14 rounded-xl object-contain flex-shrink-0"
                style={{
                  background: 'linear-gradient(180deg, rgba(255,255,255,0.06), rgba(255,255,255,0.02))',
                  border: '1px solid rgba(212,175,55,0.22)',
                  padding: '6px',
                  boxShadow: '0 0 22px rgba(212,175,55,0.08)',
                }}
                data-testid="cv-company-logo"
                onError={(e) => { e.currentTarget.style.display = 'none'; }}
              />
            ) : null}
            {/* One vertical stack: eyebrow → event detail line → client name. */}
            <div className="min-w-0 flex-1">
              <p className="text-[10px] sm:text-[11px] tracking-[0.28em] uppercase font-medium">
                {data.companyName ? (
                  <>
                    <span className="text-[#D4AF37]">{data.companyName}</span>
                    <span className="text-[#D4AF37]"> · </span>
                    <span style={{ color: '#D7DBE3' }}>{headerLabel}</span>
                  </>
                ) : (
                  <span style={{ color: '#D7DBE3' }}>{headerLabel}</span>
                )}
              </p>
              <div
                className="mt-2.5 text-[12px] sm:text-[13px] text-white/55 whitespace-nowrap overflow-hidden text-ellipsis"
                data-testid="cv-event-meta"
              >
                {(() => {
                  const ev = data.event || {};
                  const fmtDate = (raw) => {
                    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(raw || '');
                    if (!m) return raw || '';
                    const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
                    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
                  };
                  const to12h = (t) => {
                    const mm = /^(\d{1,2}):(\d{2})/.exec(t || '');
                    if (!mm) return '';
                    let h = parseInt(mm[1], 10);
                    const ap = h >= 12 ? 'PM' : 'AM';
                    h = h % 12 || 12;
                    return `${h}:${mm[2]} ${ap}`;
                  };
                  const s = to12h(ev.startTime); const e = to12h(ev.endTime);
                  const range = s && e ? `${s.replace(/ (AM|PM)$/, '')}–${e}` : (s || e || '');
                  const parts = [ev.eventType || '', fmtDate(ev.eventDate), range].filter(Boolean);
                  return parts.map((p, i) => (
                    <React.Fragment key={p + i}>
                      {i > 0 && <span className="text-[#D4AF37] mx-1.5">·</span>}
                      {p}
                    </React.Fragment>
                  ));
                })()}
              </div>
              <h1
                className="text-[42px] sm:text-[44px] font-medium tracking-tight text-[#FBF8F1] mt-1.5 pb-[18px]"
                style={{ fontFamily: 'Fraunces, ui-serif, Georgia, serif', lineHeight: 1, borderBottom: '1px solid rgba(255,255,255,0.07)' }}
                data-testid="cv-client-name"
              >
                {data.clientName}
              </h1>
            </div>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-[640px] mx-auto px-4 sm:px-6 py-7 sm:py-10 pb-28 sm:pb-16">
        {/* ── CLIENT MOBILE SCREENS (approved spec, narrow widths only) ──
            Header -> hero -> information-only stepper -> screen panels, with
            exactly one primary action in the sticky bar. Existing handlers only. */}
        {cvmOn && (() => {
          const ev = data.event || {};
          const stepKey = activeDoc === 'payments' ? 'payment' : activeDoc;
          const { steps, remaining } = buildSteps(skippedStepKeys, stepKey);
          const screenLabel = activeDoc === 'proposal' ? 'Your Proposal'
            : activeDoc === 'contract' ? 'Your Contract'
            : activeDoc === 'payments' ? 'Your Payment' : 'Your Planning';
          const nextLabel = activeDoc === 'proposal' ? 'review & approve your proposal'
            : activeDoc === 'contract' ? 'sign your contract'
            : activeDoc === 'payments' ? 'pay your retainer' : 'share your event details (anytime)';
          const lead = activeDoc === 'planning'
            ? "Last step — and this one's optional"
            : `You're ${remaining} ${remaining === 1 ? 'step' : 'steps'} away from locking in your date`;
          const chrome = (
            <ClientMobileChrome
              companyName={data.companyName || ''}
              screenLabel={screenLabel}
              eventType={ev.eventType || ''}
              eventDate={fmtEventDate(ev.eventDate)}
              timeRange={fmtTimeRange(ev.startTime, ev.endTime)}
              clientName={data.clientName || ''}
              targetMs={eventStartMs(ev.eventDate, ev.startTime)}
              steps={steps}
              lead={lead}
              nextLabel={nextLabel}
            />
          );
          if (activeDoc === 'proposal') {
            const included = (Array.isArray(data.proposal?.serviceItems) ? data.proposal.serviceItems : [])
              .map((it) => (typeof it === 'string' ? it : (it?.name || it?.title || it?.service || '')))
              .filter(Boolean);
            const total = data.proposal?.totalAmount ?? ev.contractAmount;
            return (
              <div className="cvm" data-testid="cvm-proposal">
                {chrome}
                {included.length > 0 && <IncludedList items={included} />}
                {total != null && (
                  <InvestmentPanel
                    amount={formatMoney(Number(total) + selectedAddOnsTotal)}
                    note={selectedAddOnsTotal > 0
                      ? `Includes ${formatMoney(selectedAddOnsTotal, { decimals: 0 })} in enhancements.`
                      : undefined}
                  />
                )}
                <MobileStickyBar
                  label="Review my experience"
                  onPrimary={() => setReviewOpen(true)}
                  quiet="I'd like some changes first"
                  onQuiet={() => { setChangesOpen(true); setReviewOpen(true); }}
                  testid="cvm-proposal-review-btn"
                />
              </div>
            );
          }
          if (activeDoc === 'contract' || activeDoc === 'payments') {
            // The live contract / payments body below owns the rest of the
            // screen — only the chrome comes from here.
            return <div className="cvm" data-testid={`cvm-${activeDoc}`}>{chrome}</div>;
          }
          // Planning — optional, never blocking. The real questionnaire renders
          // inside the section shell below the tabs.
          return (
            <div className="cvm" data-testid="cvm-planning">
              {chrome}
              <OptionalNote />
              <PlanningTabs tabs={['General', 'Highlights', 'Music']} active={planningTab} onSelect={setPlanningTab} />
            </div>
          );
        })()}
        {/* S123 — live event countdown (client showpiece) */}
        <div className={`mb-6${cvmOn ? ' cvm-hide-mobile' : ''}`} data-testid="cv-countdown-mount">
          <EventCountdown event={data.event} variant="card" />
        </div>
        {/* Planner read-only banner (only when the planner opened THEIR link) */}
        {data.readOnly && (
          <div
            className="mb-6 rounded-2xl px-4 sm:px-5 py-3.5 flex items-start gap-3"
            style={{
              background: 'linear-gradient(180deg, rgba(212,175,55,0.10), rgba(212,175,55,0.04))',
              border: '1px solid rgba(212,175,55,0.28)',
            }}
            data-testid="cv-planner-readonly-banner"
          >
            <div
              className="w-8 h-8 shrink-0 rounded-full flex items-center justify-center mt-0.5"
              style={{ background: 'rgba(212,175,55,0.14)' }}
            >
              <FileText className="w-4 h-4 text-[#E6C86A]" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[13px] sm:text-[14px] font-semibold text-[#F1D27A]">
                You&apos;re viewing as the planner — read only.
              </p>
              <p className="text-[12px] sm:text-[12.5px] text-white/55 leading-relaxed mt-0.5">
                Approving the proposal and signing the contract are the client&apos;s to complete.
              </p>
            </div>
          </div>
        )}

        {/* Tier 6B.2 — Client progress bar */}
        <div className={cvmOn ? 'cvm-hide-mobile' : ''}>
        <ClientProgressBar
          proposalApproved={proposalApproved}
          contractApproved={contractApproved}
          planningRequired={planningInPlay}
          planningDone={Boolean(planningDone)}
          eventId={data.eventId}
          portalToken={token}
          skippedSteps={skippedStepKeys}
          // 2026-08-09 (Andrea round 4): the stepper had its OWN payment
          // inference (retainer-threshold-crossed) that ticked "Payment"
          // done even when the overall balance was still outstanding. That
          // path bypassed the `paidInFull` + `paymentBalance` gate we
          // apply to the tab pill and the "What Happens Next" roadmap.
          // Pass an authoritative override so all three surfaces evaluate
          // the same expression. Fail closed while the payload is still
          // loading (both fields nullish) — leave override null and let
          // the internal fallback keep the step locked.
          paymentCompleteOverride={
            (data.paidInFull != null || data.paymentBalance != null)
              ? Boolean(data.paidInFull) && (Number(data.paymentBalance ?? 0) <= 0.005)
              : null
          }
          nextStepKeyOverride={nextStepKey}
        />
        </div>

        {/* Client Update card — never on the Payment or Proposal screens:
            everything it says is already on those screens. */}
        {activeDoc !== 'payments' && activeDoc !== 'proposal' && (
        <div
          className={`rounded-[28px] p-6 sm:p-8 mb-8${cvmOn && activeDoc === 'proposal' ? ' cvm-hide-mobile' : ''}`}
          style={{
            background: 'linear-gradient(180deg, rgba(255,255,255,0.05), rgba(255,255,255,0.02))',
            border: '1px solid rgba(212,175,55,0.18)',
            boxShadow: '0 8px 40px rgba(0,0,0,0.45), 0 0 0 1px rgba(212,175,55,0.04), 0 0 40px rgba(212,175,55,0.05)',
          }}
          data-testid="cv-message"
        >
          <div className="mb-4 sm:mb-5 flex items-start justify-between gap-4 flex-wrap">
            <div>
              <div className="text-[11px] uppercase tracking-[0.28em] text-[#D4AF37]/75 font-medium">
                Update
              </div>
              <h2 className="mt-2 text-xl sm:text-2xl font-semibold tracking-tight text-white">
                {updateHeadline}
              </h2>
            </div>
            {nextStepPill && (
              <div
                className="rounded-full border border-[#D4AF37]/25 bg-[#D4AF37]/10 px-3.5 py-1.5 text-[11px] sm:text-xs font-medium text-[#E7C96A] whitespace-nowrap"
                style={{ boxShadow: '0 0 22px rgba(212,175,55,0.14)' }}
                data-testid="cv-next-step-pill"
              >
                {nextStepPill}
              </div>
            )}
          </div>
          <p className="text-[15px] sm:text-[16px] leading-7 text-white/70 whitespace-pre-wrap">
            {dynamicMessage}
          </p>
        </div>
        )}

        {/* Document tabs. Hidden ONLY when there is exactly one document and
            the client is already on it — see docTabsVisibleFor. Any other
            state renders the strip: hiding it while a client sits on a
            document that isn't in the list is how one got stranded. */}
        {docTabsVisible && (
        <div
          className="mb-8 rounded-[24px] p-2 backdrop-blur"
          style={{
            background: 'rgba(255,255,255,0.025)',
            border: '1px solid rgba(255,255,255,0.08)',
            boxShadow: '0 10px 30px rgba(0,0,0,0.28)',
          }}
          data-testid="cv-doc-tabs"
        >
          <div
            className="flex flex-wrap sm:grid gap-2 text-sm"
            style={{
              gridTemplateColumns: `repeat(${docTabs.length}, minmax(0, 1fr))`,
            }}
          >
          {docTabs.map(tab => {
            const isActive = activeDoc === tab.key;
            const Icon = tab.icon;
            if (tab.locked) {
              const hint = tab.lockedHint || 'Finish the step before this one first.';
              return (
                <div
                  key={tab.key}
                  onClick={() => toast(hint)}
                  className="group relative grow basis-[47%] sm:basis-auto rounded-[18px] px-3 sm:px-4 py-3.5 sm:py-4 cursor-not-allowed select-none text-white/30"
                  title={hint}
                  aria-disabled="true"
                  data-testid={`cv-tab-${tab.key}`}
                  data-locked="true"
                >
                  <span className="relative z-10 flex items-center justify-center gap-1.5 sm:gap-2 text-[12px] sm:text-[13px] font-medium">
                    <Lock className="w-3.5 h-3.5" />
                    <span>{tab.label}</span>
                  </span>
                  <span
                    className="relative z-10 mt-1 block text-center text-[10px] sm:text-[10.5px] leading-tight text-white/25"
                    data-testid={`cv-tab-${tab.key}-locked-hint`}
                  >
                    {hint}
                  </span>
                </div>
              );
            }
            return (
              <button
                key={tab.key}
                onClick={() => setRequestedDoc(tab.key)}
                className={`group relative grow basis-[47%] sm:basis-auto rounded-[18px] px-3 sm:px-4 py-3.5 sm:py-4 transition-all duration-300 ${
                  isActive
                    ? 'text-[#F1D27A]'
                    : 'text-white/46 hover:bg-white/[0.04] hover:text-white/75'
                }`}
                style={
                  isActive
                    ? {
                        background: 'linear-gradient(180deg, rgba(212,175,55,0.16), rgba(212,175,55,0.05))',
                        boxShadow: 'inset 0 -1px 0 rgba(212,175,55,0.3), 0 0 24px rgba(212,175,55,0.08)',
                      }
                    : {}
                }
                data-testid={`cv-tab-${tab.key}`}
              >
                <span className="relative z-10 flex items-center justify-center gap-1.5 sm:gap-2 text-[12px] sm:text-[13px] font-medium">
                  <Icon className="w-4 h-4" />
                  <span>{tab.label}</span>
                  {tab.approved && <Check className="w-3.5 h-3.5 text-emerald-400" />}
                </span>
                {isActive && (
                  <span
                    className="absolute inset-x-5 bottom-0 h-px"
                    style={{ background: 'linear-gradient(90deg, transparent, #D4AF37, transparent)' }}
                  />
                )}
              </button>
            );
          })}
          </div>
        </div>
        )}

        {/* Document sections */}
        {activeDoc === 'payments' ? (
          /* ── PAYMENTS TAB ── */
          <ClientPaymentView
            eventId={data?.eventId}
            portalToken={token}
            paymentIncentive={data?.paymentIncentive}
            eventDate={data?.event?.eventDate}
          />
        ) : activeDoc === 'planning' ? (
          /* ── DYNAMIC PLANNING TAB ── */
          <div data-testid="cv-planning-content">
            {(() => {
              const eventType = data.event?.eventType || 'party';
              const planning = data.planning || {};
              // The schema key recorded when this client's answers were first
              // saved wins over a fresh resolution — their form must not
              // change shape mid-planning. See resolvePlanningSchemaKey.
              const schema = getPlanningSchema(eventType, { recordedSchemaKey: planning.eventType });
              const isSubmitted = planning.status === 'submitted' || planning.submittedAt;
              const existingAnswers = isSubmitted
                ? { ...(planning.answers || {}), _submitted: true }
                : (planning.answers || {});

              return (
                <div className="space-y-4">
                  {/* Admin "Preview as client" — look-only banner + a jump into
                      the real "Fill in for client" mode (preview never saves). */}
                  {(previewMode && (Boolean(data.previewOnly) || new URLSearchParams(window.location.search).get('preview') === '1')) && (
                    <div
                      className="rounded-xl px-4 py-3.5 flex items-start gap-3"
                      style={{ background: 'rgba(212,175,55,0.06)', border: '1px solid rgba(212,175,55,0.22)' }}
                      data-testid="cv-planning-preview-banner"
                    >
                      <Eye className="w-4 h-4 text-[#E6C86A] shrink-0 mt-0.5" />
                      <div className="flex-1 min-w-0">
                        <p className="text-[13px] text-white/70 leading-relaxed">
                          <span className="font-semibold text-[#F1D27A]">Preview only</span> — this is exactly what your client sees. Nothing typed here is saved. To enter answers yourself, use "Fill in for client".
                        </p>
                        {data.eventId && (
                          <button
                            type="button"
                            onClick={() => { window.location.href = `/event/${data.eventId}?section=planning&fillIn=1`; }}
                            className="mt-2.5 inline-flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-[12px] font-semibold transition bg-[#D4AF37]/12 border border-[#D4AF37]/35 text-[#E7C75E] hover:-translate-y-px"
                            data-testid="cv-planning-fill-in-btn"
                          >
                            <Pencil className="w-3.5 h-3.5" /> Fill in for client
                          </button>
                        )}
                      </div>
                    </div>
                  )}
                  {/* Intro message */}
                  {!isSubmitted && !data.planningLocked && (
                    <div
                      className="rounded-xl px-4 py-3"
                      style={{ background: 'rgba(212,175,55,0.04)', border: '1px solid rgba(212,175,55,0.1)' }}
                    >
                      <p className="text-[13px] text-white/45 leading-relaxed">{schema.description}</p>
                    </div>
                  )}
                  <PlanningFormRenderer
                    schema={schema}
                    token={token}
                    initialAnswers={existingAnswers}
                    initialStepId={planning.currentStep || null}
                    readOnly={false}
                    previewOnly={previewMode || new URLSearchParams(window.location.search).get('preview') === '1'}
                    eventDate={data?.event?.eventDate}
                    booking={{ eventDate: data.event?.eventDate, startTime: data.event?.startTime, endTime: data.event?.endTime, venue: data.event?.venue }}
                    companyIndustry={data?.companyIndustry || ''}
                    songRequestLink={data?.songRequestLink || ''}
                    locked={!!data?.planningLocked}
                    lockContact={data?.planningLockContact || null}
                    onSubmitted={(planData) => setData(prev => ({
                      ...prev,
                      planning: planData,
                      planningSubmitted: true,
                    }))}
                  />
                </div>
              );
            })()}
          </div>
        ) : docData ? (
          <div className={`space-y-3${cvmOn && activeDoc === 'proposal' ? ' cvm-hide-mobile' : ''}`} data-testid={`cv-${activeDoc}-content`}>
            {/* ── Investment Preview hero — proposal only ─────────────── */}
            {activeDoc === 'proposal' && (() => {
              const evt = data.event || {};
              const amount = evt.contractAmount || 0;
              const retainer = evt.retainerAmount || 0;
              return (
                <div
                  className="rounded-[28px] p-6 sm:p-8 mb-4"
                  style={{
                    background: 'linear-gradient(180deg, rgba(255,255,255,0.05), rgba(255,255,255,0.02))',
                    border: '1px solid rgba(212,175,55,0.18)',
                    boxShadow: '0 8px 40px rgba(0,0,0,0.45), 0 0 0 1px rgba(212,175,55,0.04), 0 0 40px rgba(212,175,55,0.06)',
                  }}
                  data-testid="cv-investment-preview"
                >
                  <div className="flex items-start justify-between gap-4 flex-wrap mb-5">
                    <div>
                      <h2 className="text-xl sm:text-2xl font-semibold tracking-tight text-white">
                        Your {evt.eventType || 'Event'} Proposal
                      </h2>
                    </div>
                  </div>
                </div>
              );
            })()}

            {/* Tier 6E.1 — Urgency layer (proposal only, hide once approved). 
                Subtle availability + decision-window cues; no countdown,
                no alarm colors. Sits directly above the package tiers
                so the prospect reads it right before tier selection. */}
            {activeDoc === 'proposal' && !proposalApproved && (
              <ProposalUrgencyNote />
            )}

            {/* Tier 6D.1 — Package anchoring removed */}

            {/* ── PRINT ONLY ── A printed agreement opens like a document, not
                like an app screen: title block, then the parties and the event
                as plain facts. Invisible on screen (.cvp-print-only). */}
            {activeDoc === 'contract' && docData && (() => {
              const ev = data.event || {};
              const rows = [
                ['Provider', brandName],
                ['Client', [ev.fullName || data.clientName, ev.email || data.clientEmail].filter(Boolean).join(' · ')],
                ['Event', ev.eventType],
                ['Date', ev.eventDate ? formatDate(ev.eventDate) : ''],
                ['Time', fmtTimeRange(ev.startTime, ev.endTime)],
                ['Venue', ev.venue],
              ].filter(([, v]) => String(v || '').trim());
              return (
                <>
                  <div className="cvp-print-only cvp-print-head" aria-hidden="true">
                    <div className="brand">{brandName}</div>
                    <div className="title">Event Agreement</div>
                  </div>
                  <table className="cvp-print-only cvp-print-facts" aria-hidden="true">
                    <tbody>
                      {rows.map(([k, v]) => (
                        <tr key={k}><th>{k}</th><td>{v}</td></tr>
                      ))}
                    </tbody>
                  </table>
                </>
              );
            })()}

            {/* ── Contract: one-line deal summary ──
                The parties preamble and the event-details clause are legal
                prose; the one fact a client scans is who/what/when/where, so
                it reads as a single line here and the prose itself lives,
                unchanged, inside "Read the full terms". */}
            {activeDoc === 'contract' && docData && (() => {
              const ev = data.event || {};
              const who = [brandName, ev.fullName || data.clientName].filter(Boolean).join(' and ');
              const niceDate = (() => {
                const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(ev.eventDate || '');
                if (!m) return ev.eventDate ? formatDate(ev.eventDate) : '';
                return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]))
                  .toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
              })();
              const what = [ev.eventType, niceDate, ev.venue]
                .filter((v) => String(v || '').trim()).join(', ');
              if (!who && !what) return null;
              return (
                <p
                  className="text-[13px] sm:text-[13.5px] text-white/55 leading-relaxed"
                  data-testid="cv-contract-deal-summary"
                >
                  {who ? `Agreement between ${who}` : 'Agreement'}{what ? ` — ${what}` : ''}
                </p>
              );
            })()}

            {sections.map(sec => {
              // Legal prose is relocated, unchanged, into the full-terms
              // accordion rendered after the document.
              if (activeDoc === 'contract' && CONTRACT_LEGAL_KEYS.includes(sec.key)) return null;
              // For the contract's plain "signatureSection" text, we DO NOT render it in the scroll list
              // — a much nicer signature UI is rendered separately (signed block above OR approval card below).
              if (sec.key === 'signatureSection' && activeDoc === 'contract' && !contractApproved) {
                return null;
              }

              // For signed contracts: replace signature section with actual signed data
              if (sec.key === 'signatureSection' && activeDoc === 'contract' && contractApproved && data.signerName) {
                const signedAt = data.contractApprovedAt ? new Date(data.contractApprovedAt) : new Date();
                const signedDateStr = formatDate(signedAt);
                const signedTimeStr = signedAt.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
                return (
                  <div
                    key={sec.key}
                    className="rounded-xl overflow-hidden"
                    style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.05)' }}
                    data-testid="cv-signed-signature-section"
                  >
                    {/* Header: Title + Fully Executed Seal */}
                    <div className="flex items-center justify-between px-4 py-3" style={{ borderBottom: '1px solid rgba(255,255,255,0.03)' }}>
                      <div className="flex items-center gap-2">
                        <PenTool className="w-3.5 h-3.5 text-[#D4AF37]/40" />
                        <span className="text-[11px] font-semibold text-white/50 tracking-wide uppercase">Signature & Approval</span>
                      </div>
                      <span
                        className="text-[9px] px-3 py-1 rounded-full font-bold tracking-[0.25em] uppercase flex items-center gap-1.5"
                        style={{ background: 'rgba(34,197,94,0.12)', border: '1.5px solid rgba(34,197,94,0.4)', color: '#22C55E' }}
                        data-testid="cv-fully-executed-seal"
                      >
                        <CheckCircle2 className="w-3 h-3" /> Fully Executed
                      </span>
                    </div>

                    <div className="px-4 py-4 space-y-4">
                      <p className="text-[12px] text-white/35 leading-relaxed">By signing below, both parties acknowledge and agree to the terms outlined in this Agreement.</p>

                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        {/* Provider — auto-countersigned */}
                        <div className="rounded-lg px-4 py-3" style={{ background: 'rgba(212,175,55,0.04)', border: '1px solid rgba(212,175,55,0.12)' }}>
                          <p className="text-[9px] text-[#D4AF37]/50 tracking-widest uppercase mb-1.5 font-semibold">Provider</p>
                          <div className="flex items-center justify-center rounded-md mb-2" style={{ background: '#ffffff', minHeight: '48px', padding: '6px' }}>
                            <span style={{ fontFamily: '"Dancing Script", "Brush Script MT", cursive', fontSize: '22px', color: '#0A0E18', fontStyle: 'italic', lineHeight: 1 }}>
                              {brandName}
                            </span>
                          </div>
                          <p className="text-[12px] text-white/55 font-medium">{brandName}</p>
                          <p className="text-[9px] text-white/25 mt-0.5">Countersigned · {signedDateStr}</p>
                        </div>

                        {/* Client — signed (focal point) */}
                        <div className="rounded-lg px-4 py-3" style={{ background: 'rgba(34,197,94,0.03)', border: '1px solid rgba(34,197,94,0.12)' }}>
                          <p className="text-[9px] text-[#22C55E]/50 tracking-widest uppercase mb-1.5 font-semibold">Client</p>
                          <div className="flex items-center justify-center rounded-md mb-2" style={{ background: '#ffffff', minHeight: '48px', padding: '6px' }}>
                            {data.signatureImage ? (
                              <img src={data.signatureImage} alt="Signature" style={{ maxHeight: '40px', maxWidth: '100%' }} />
                            ) : (
                              <span style={{ fontFamily: '"Dancing Script", "Brush Script MT", cursive', fontSize: '22px', color: '#0A0E18', fontStyle: 'italic', lineHeight: 1 }}>
                                {data.signerName}
                              </span>
                            )}
                          </div>
                          <p className="text-[12px] text-white/70 font-medium">{data.signerName}</p>
                          <p className="text-[9px] text-white/25 mt-0.5">Signed · {signedDateStr} at {signedTimeStr}</p>
                        </div>
                      </div>

                      {/* What Happens Next roadmap */}
                      <div className="pt-2" data-testid="cv-next-steps-roadmap">
                        <p className="text-[9px] text-white/20 tracking-[0.25em] uppercase font-bold mb-2">What Happens Next</p>
                        <div className="flex items-center gap-1.5 overflow-x-auto">
                          {(() => {
                            // Belt-and-suspenders gate on the Payment step
                            // (2026-08-08 pm follow-up): a tick renders ONLY
                            // when BOTH `paidInFull` is true AND the raw
                            // outstanding balance is zero. Backend exposes
                            // both fields — if they ever disagree, treat
                            // the number as the truth. Same rule applied
                            // to the Payments tab pill above.
                            const _paymentDone = Boolean(data.paidInFull) && (Number(data.paymentBalance ?? 0) <= 0.005);
                            return [
                              { label: 'Contract', done: true },
                              { label: 'Payment', done: _paymentDone },
                              // A skipped module is invisible: the roadmap
                              // drops the entry rather than showing it as an
                              // incomplete step the client still owes.
                              ...(planningInPlay ? [{ label: 'Planning', done: Boolean(planningDone) }] : []),
                              { label: 'Event Day', done: false },
                            ];
                          })().map((s, i, arr) => (
                            <React.Fragment key={s.label}>
                              <div className="flex items-center gap-1.5 shrink-0">
                                <div className="w-4 h-4 rounded-full flex items-center justify-center shrink-0" style={{
                                  background: s.done ? 'rgba(34,197,94,0.25)' : (s.current ? 'rgba(212,175,55,0.2)' : 'rgba(255,255,255,0.05)'),
                                  border: `1px solid ${s.done ? 'rgba(34,197,94,0.5)' : (s.current ? 'rgba(212,175,55,0.5)' : 'rgba(255,255,255,0.08)')}`,
                                }}>
                                  {s.done && <Check className="w-2.5 h-2.5 text-[#22C55E]" />}
                                </div>
                                <span className="text-[10px] font-medium whitespace-nowrap" style={{ color: s.done ? 'rgba(34,197,94,0.8)' : (s.current ? '#D4AF37' : 'rgba(255,255,255,0.3)') }}>{s.label}</span>
                              </div>
                              {i < arr.length - 1 && <div className="h-px w-4 shrink-0" style={{ background: s.done ? 'rgba(34,197,94,0.3)' : 'rgba(255,255,255,0.05)' }} />}
                            </React.Fragment>
                          ))}
                        </div>
                      </div>

                      {/* Integrated approved confirmation */}
                      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between pt-2" style={{ borderTop: '1px solid rgba(255,255,255,0.03)' }}>
                        <div className="flex items-center gap-2">
                          <div className="w-5 h-5 rounded-md flex items-center justify-center" style={{ background: 'rgba(34,197,94,0.1)' }}>
                            <CheckCircle2 className="w-3 h-3 text-[#22C55E]/70" />
                          </div>
                          <div>
                            <p className="text-[11px] text-[#22C55E]/60 font-medium">Contract Approved</p>
                            <p className="text-[9px] text-white/15">{signedDateStr}, {signedTimeStr}</p>
                          </div>
                        </div>
                        <div className="flex flex-wrap items-center gap-2 sm:flex-nowrap w-full sm:w-auto">
                          <button
                            onClick={() => {
                              const url = `${API}/api/documents/contracts/by-token/${token}/pdf`;
                              // Trigger download
                              const a = document.createElement('a');
                              a.href = url;
                              a.download = '';
                              document.body.appendChild(a);
                              a.click();
                              a.remove();
                            }}
                            className="flex-1 sm:flex-none min-w-[120px] sm:min-w-0 min-h-[44px] sm:min-h-0 justify-center flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[10px] font-medium transition-all hover:-translate-y-px"
                            style={{ background: 'rgba(212,175,55,0.08)', border: '1px solid rgba(212,175,55,0.18)', color: '#D4AF37' }}
                            data-testid="cv-download-contract-btn"
                          >
                            <Download className="w-3 h-3" />
                            Download PDF
                          </button>
                          <button
                            onClick={printContractDocument}
                            className="flex-1 sm:flex-none min-w-[120px] sm:min-w-0 min-h-[44px] sm:min-h-0 justify-center flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[10px] font-medium transition-all hover:-translate-y-px"
                            style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)', color: 'rgba(255,255,255,0.3)' }}
                            data-testid="cv-print-contract-btn"
                          >
                            <Printer className="w-3 h-3" />
                            Print
                          </button>
                          <button
                            onClick={() => {
                              const url = `${API}/api/documents/contracts/by-token/${token}/certificate/pdf`;
                              const a = document.createElement('a'); a.href = url; a.download = '';
                              document.body.appendChild(a); a.click(); a.remove();
                            }}
                            className="flex-1 sm:flex-none min-w-[120px] sm:min-w-0 min-h-[44px] sm:min-h-0 justify-center flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[10px] font-medium transition-all hover:-translate-y-px"
                            style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)', color: 'rgba(255,255,255,0.35)' }}
                            data-testid="cv-download-certificate-btn"
                            title="Download signature certificate (audit trail with IP + timestamp)"
                          >
                            <Award className="w-3 h-3" />
                            Signing certificate
                          </button>
                        </div>
                      </div>

                      {/* Legal disclosure */}
                      <p className="text-[9px] text-white/20 leading-relaxed pt-1" data-testid="cv-legal-disclosure">
                        By signing, both parties agree this electronic signature is the legal equivalent of a handwritten signature under the U.S. E-SIGN Act.
                      </p>
                      {/* PRINT ONLY — the audit line belongs on the paper copy. */}
                      <p className="cvp-print-only" style={{ fontSize: '8.5pt', marginTop: '6px' }} aria-hidden="true">
                        Signer&apos;s IP address and signing time are recorded in the signature audit trail.
                      </p>
                    </div>
                  </div>
                );
              }
              if (sec.key === 'pricingStructure' && activeDoc === 'proposal') {
                const _evt = data.event || {};
                const _amount = _evt.contractAmount || 0;
                const _retainer = _evt.retainerAmount || 0;
                // The figure a client watches while they shop: the frozen base
                // plus whatever is selected right now. Re-derived on every
                // render, so adding or removing an enhancement moves it in the
                // same tap. The server still owns the recorded number.
                const _shownTotal = _amount + selectedAddOnsTotal;
                return (
                  <div key={sec.key} className="space-y-3">
                    <div
                      className="rounded-[24px] p-6 sm:p-7"
                      style={{
                        background: 'linear-gradient(180deg, rgba(255,255,255,0.05), rgba(255,255,255,0.02))',
                        border: '1px solid rgba(212,175,55,0.18)',
                        boxShadow: '0 8px 40px rgba(0,0,0,0.45), 0 0 0 1px rgba(212,175,55,0.04), 0 0 40px rgba(212,175,55,0.06)',
                      }}
                      data-testid="cv-investment-total"
                    >
                      <p className="text-[11px] uppercase tracking-[0.28em] text-[#D4AF37]/75 font-medium">
                        Your Investment
                      </p>
                      <div className="flex items-end gap-6 flex-wrap mt-2">
                        <p
                          className="text-4xl sm:text-5xl font-semibold tracking-tight tabular-nums"
                          style={{
                            background: 'linear-gradient(180deg, #F2D989 0%, #D4AF37 55%, #B88B1A 100%)',
                            WebkitBackgroundClip: 'text',
                            WebkitTextFillColor: 'transparent',
                            backgroundClip: 'text',
                            transition: 'opacity .25s ease',
                          }}
                          data-testid="cv-investment-live-total"
                        >
                          {_amount ? formatMoney(_shownTotal) : 'Custom Quote'}
                        </p>
                      </div>
                      {_amount > 0 && selectedAddOnsTotal > 0 && (
                        <p className="mt-2 text-[12.5px] text-white/55" data-testid="cv-investment-enhancements-note">
                          {formatMoney(_amount)} package + {formatMoney(selectedAddOnsTotal, { decimals: 0 })} in enhancements
                        </p>
                      )}
                      {/* ONE quiet line instead of a second retainer block —
                          the full schedule lives on the Payment stage. */}
                      {_retainer > 0 && (
                        <p className="mt-3 text-[12.5px] text-white/45 leading-relaxed" data-testid="cv-investment-retainer-note">
                          Secured with a {formatMoney(_retainer).replace(/\.00$/, '')} retainer — balance on a simple schedule.
                        </p>
                      )}
                    </div>
                  </div>
                );
              }
              if (sec.key === 'investment') {
                // Standalone dollar summary — the ONE reliable place the total,
                // deposit, and balance appear in the portal, regardless of the
                // tenant's Payment Terms wording. Total is the hero; the two
                // supporting lines stay quiet on purpose.
                const _evt = data.event || {};
                const _rawTotal = docData.contractAmount ?? _evt.contractAmount;
                const _rawRet   = docData.retainerAmount ?? _evt.retainerAmount;
                let _total = Number(_rawTotal) || 0;
                let _retainer = Number(_rawRet) || 0;
                // Fallback: parse the backend's plain-text section if the amounts
                // never landed on the payload (older events / partial migrations).
                if (!_total) {
                  const text = String(docData[sec.key] || '');
                  const mT = text.match(/Total:\s*\$?([\d,]+(?:\.\d+)?)/i);
                  const mR = text.match(/Deposit\s*\/?\s*Retainer:\s*\$?([\d,]+(?:\.\d+)?)/i);
                  if (mT) _total = Number(mT[1].replace(/,/g, '')) || 0;
                  if (mR) _retainer = Number(mR[1].replace(/,/g, '')) || 0;
                }
                if (!_total) return null;
                const _balance = Math.max(0, _total - _retainer);
                const fmt = (n) => formatMoney(n);
                return (
                  <React.Fragment key={sec.key}>
                  <div
                    className="rounded-xl overflow-hidden"
                    style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.04)' }}
                    data-testid="cv-investment-block"
                  >
                    <div className="flex items-center gap-2 px-4 py-3" style={{ borderBottom: '1px solid rgba(255,255,255,0.03)' }}>
                      <sec.icon className="w-3.5 h-3.5 text-[#D4AF37]/70" />
                      <span className="text-[11px] font-semibold text-[#D4AF37]/90 tracking-wide uppercase">{sec.label}</span>
                    </div>
                    <div className="px-4 py-5 space-y-4">
                      <div>
                        <p className="text-[10px] uppercase tracking-[0.24em] text-white/40 font-medium">Event total</p>
                        <p
                          className="mt-1 text-3xl sm:text-4xl font-semibold tabular-nums leading-none"
                          style={{ color: '#D4AF37' }}
                          data-testid="cv-investment-total-hero"
                        >
                          {fmt(_total)}
                        </p>
                      </div>
                      <div className="space-y-1.5 pt-1">
                        <div className="flex items-baseline gap-2 text-[13px]">
                          <span className="min-w-[9rem]" style={{ color: '#E8CE7A', fontWeight: 600 }}>
                            Due now
                            <small className="block text-[10.5px] tracking-[0.14em] uppercase font-bold mt-0.5" style={{ color: '#D4AF37' }}>Retainer</small>
                          </span>
                          <span className="tabular-nums" style={{ color: '#E8CE7A', fontFamily: 'Fraunces, ui-serif, Georgia, serif', fontSize: '19px' }}>{fmt(_retainer)}</span>
                          <span className="text-white/35 text-[11px]">(due upon signing)</span>
                        </div>
                        <div className="flex items-baseline gap-2 text-[13px]">
                          <span className="text-white/60 min-w-[9rem]">Balance</span>
                          <span className="text-white tabular-nums">{fmt(_balance)}</span>
                          <span className="text-white/35 text-[11px]">(due before the event date)</span>
                        </div>
                      </div>
                    </div>
                  </div>
                  {/* The short version lives after the whole document now, with
                      the full legal prose folded into its accordion. */}
                  </React.Fragment>
                );
              }
              // ONE rule, shared with the admin editor. See lib/proposalDisplay.js.
              // The stored value always wins. Generated items appear only when
              // nothing was saved at all. This applies to the contract's
              // "Services Included" section as well, which renders through this
              // same loop.
              // Priced table when the admin turned prices on AND the lines
              // reconcile with the total. Same guard as the workspace and the
              // printed contract. '' falls through to the rule that has always
              // governed this section.
              const secPriced =
                sec.key === 'servicesIncluded' && docData.servicesIsText !== true
                  ? linesToPricedHtml(
                      linesFrom(docData),
                      docData.totalAmount ?? docData.contractAmount,
                      docData.showLinePrices,
                    )
                  : '';
              const secContent =
                sec.key === 'servicesIncluded'
                  ? (docData.servicesIsText === true
                      ? resolveServicesIncluded(docData[sec.key], docData.serviceItems)
                      : (secPriced
                          || linesToTitledHtml(linesFrom(docData))
                          || resolveServicesIncluded(docData[sec.key], docData.serviceItems)))
                  : docData[sec.key];
              // The client proposal keeps a short overview — this is read on a
              // phone, and the package list below carries the detail.
              const secText = (activeDoc === 'proposal' && sec.key === 'eventOverview'
                && typeof secContent === 'string' && !secContent.includes('<'))
                ? (secContent.trim().split(/(?<=[.!?])\s+/).slice(0, 2).join(' ') || secContent)
                : secContent;
              return <Section key={sec.key} icon={sec.icon} label={sec.label} content={secText} collapsible={['paymentTerms', 'cancellationPolicy', 'liabilityClause', 'additionalTerms'].includes(sec.key)} />;
            })}

            {/* ── The short version + the full legal terms ──
                The three plain-language bullets stay visible; every legal
                paragraph above moved in here VERBATIM, one tap away. */}
            {activeDoc === 'contract' && docData && (
              <div className="brief" data-testid="cv-contract-brief">
                <p className="t">The short version</p>
                <ul>
                  <li><svg viewBox="0 0 24 24"><path d="M20 6L9 17l-5-5" /></svg>We hold your date once the retainer is paid.</li>
                  <li><svg viewBox="0 0 24 24"><path d="M20 6L9 17l-5-5" /></svg>Balance is due two weeks before the event.</li>
                  <li><svg viewBox="0 0 24 24"><path d="M20 6L9 17l-5-5" /></svg>Free to reschedule up to 30 days out.</li>
                </ul>
                <div className="acc">
                  {/* Open/closed is React state with inline collapse styles —
                      the previous version relied on a stylesheet class, and
                      when that rule did not reach the production build the
                      legal prose rendered as visible text. */}
                  <div
                    className="acc-h"
                    role="button"
                    tabIndex={0}
                    onClick={() => setTermsOpen((v) => !v)}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setTermsOpen((v) => !v); } }}
                    data-testid="cv-contract-terms-toggle"
                  >
                    Read the full terms
                    <svg viewBox="0 0 24 24" fill="none" style={{ transform: termsOpen ? 'rotate(90deg)' : 'none', transition: 'transform .3s cubic-bezier(.22,1,.36,1)' }}><path d="M9 6l6 6-6 6" /></svg>
                  </div>
                  <div
                    className="acc-b"
                    aria-hidden={!termsOpen}
                    style={{
                      maxHeight: termsOpen ? 6000 : 0,
                      opacity: termsOpen ? 1 : 0,
                      overflow: 'hidden',
                      visibility: termsOpen ? 'visible' : 'hidden',
                      transition: 'max-height .35s cubic-bezier(.22,1,.36,1), opacity .3s, visibility .3s',
                    }}
                  >
                    <div className="acc-b-in space-y-3" data-testid="cv-contract-full-terms">
                      {sections
                        .filter((s) => CONTRACT_LEGAL_KEYS.includes(s.key) && docData[s.key])
                        .map((s) => (
                          <Section key={s.key} icon={s.icon} label={s.label} content={docData[s.key]} />
                        ))}
                    </div>
                  </div>
                </div>
              </div>
            )}

          </div>
        ) : (
          <div className="text-center py-12" data-testid="cv-no-doc">
            <p className="text-[13px] text-white/30">
              {activeDoc === 'none'
                ? "There's nothing for you to open just yet — we'll email you as soon as your documents are ready."
                : activeDoc === 'contract'
                  ? "Your contract will appear here as soon as we send it — we'll email you."
                  : activeDoc === 'proposal'
                    ? "Your proposal will appear here as soon as we send it — we'll email you."
                    : "This will appear here as soon as we send it — we'll email you."}
            </p>
          </div>
        )}

        {/* ── Packages showcase — "What we offer" (proposal tab) ── */}
        {activeDoc === 'proposal' && docData && Array.isArray(data.packageOfferings) && data.packageOfferings.length > 0 && (
          <PackagesShowcase
            packages={data.packageOfferings}
            selectedName={data.event?.packageName}
            defaultOpen={!proposalApproved}
            token={token}
            canChoose={!proposalApproved && !data.readOnly}
          />
        )}

        {/* ── Tier 6C.1 — Package upsell layer (proposal only) ── */}
        {activeDoc === 'proposal' && docData && !proposalApproved && !data.hideAddOnsFromClient && (
          <SectionErrorBoundary section="proposal-add-ons">
            <ProposalAddOns
              eventType={data.event?.eventType}
              companyIndustry={data.companyIndustry}
              clientToken={token}
              addOns={data.proposalAddOns}
              liveAddOns={data.liveAddOns || []}
              hiddenKeys={data.clientHiddenAddOns || []}
              featuredKeys={data.clientFeaturedAddOns ?? null}
              shownKeys={data.clientShownAddOns || []}
              onReview={() => setReviewOpen(true)}
              onSelectionChange={setSelectedAddOns}
              readOnly={!!data.readOnly}
            />
          </SectionErrorBoundary>
        )}

        {/* ── Phase B — contract-stage live upgrade ──
            Renders BELOW the signature panel (see after the contract approval
            block): a client must be able to sign without scrolling past
            anything optional. */}

        {/* ── REVIEW & CONFIRM STEP ──
            Opened by the enhancements bar. The actions below are the EXACT
            proposal handlers (handleApproveProposal / handleRequestChanges) —
            no approve logic is rebuilt here. */}
        {reviewOpen && activeDoc === 'proposal' && docData && (
          <div
            className="fixed inset-0 z-[70] overflow-y-auto"
            style={{ background: 'linear-gradient(180deg,#0B1424 0%,#080C18 60%,#06090F 100%)' }}
            role="dialog"
            aria-modal="true"
            aria-label="Review and confirm"
            data-testid="cv-review-step"
          >
            <div className="max-w-[520px] mx-auto px-5 pt-6 pb-10">
              <button
                type="button"
                onClick={() => setReviewOpen(false)}
                className="inline-flex items-center gap-1.5 text-[12.5px] font-medium text-white/50 hover:text-white/80 transition-colors"
                data-testid="cv-review-back"
              >
                <ChevronLeft className="w-4 h-4" /> Back to proposal
              </button>
              <p className="mt-5 text-[10.5px] font-semibold uppercase tracking-[0.2em] text-[#D4AF37]">Review &amp; confirm</p>
              <h2
                className="mt-2 text-[27px] font-medium text-[#F2EEE6] leading-tight"
                style={{ fontFamily: 'Fraunces, ui-serif, Georgia, serif' }}
                data-testid="cv-review-title"
              >
                Your experience
              </h2>
              <div
                className="mt-6 rounded-[18px] overflow-hidden"
                style={{ background: '#0E1526', border: '1px solid rgba(255,255,255,0.07)' }}
              >
                <div className="px-5 py-4 flex items-start justify-between gap-4" style={{ borderBottom: '1px solid rgba(255,255,255,0.07)' }}>
                  <div className="min-w-0">
                    <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-white/35">Your package</p>
                    <p className="mt-1 text-[15px] font-semibold text-[#F2EEE6]" data-testid="cv-review-package-name">
                      {data.event?.packageName || 'Your custom package'}
                    </p>
                  </div>
                  <p className="text-[15px] font-semibold text-[#E8CE7A] tabular-nums flex-shrink-0" data-testid="cv-review-base-total">
                    {formatMoney(reviewBase, { decimals: 0 })}
                  </p>
                </div>
                {selectedAddOns.length > 0 && (
                  <div className="px-5 py-3" style={{ borderBottom: '1px solid rgba(255,255,255,0.07)' }} data-testid="cv-review-addons">
                    <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-white/35 mb-2">Your enhancements</p>
                    {selectedAddOns.map((a) => (
                      <div key={a.key} className="flex items-center justify-between gap-4 py-1.5" data-testid={`cv-review-addon-${a.key}`}>
                        <p className="text-[13.5px] text-[#C4CAD6] min-w-0 truncate">{a.name}</p>
                        {a.price && <p className="text-[13.5px] font-semibold text-[#E8CE7A] tabular-nums flex-shrink-0">{a.price}</p>}
                      </div>
                    ))}
                  </div>
                )}
                <div className="px-5 py-4 flex items-center justify-between gap-4">
                  <p className="text-[12.5px] font-semibold text-white/55">Total</p>
                  <p
                    className="text-[23px] text-[#E8CE7A] tabular-nums"
                    style={{ fontFamily: 'Fraunces, ui-serif, Georgia, serif' }}
                    data-testid="cv-review-grand-total"
                  >
                    {formatMoney(reviewGrandTotal, { decimals: 0 })}
                  </p>
                </div>
              </div>
              {!data.readOnly && !proposalApproved && (
                <>
                  <button
                    onClick={handleApproveProposal}
                    disabled={approvingProposal}
                    className="mt-6 w-full flex items-center justify-center gap-2 py-4 rounded-xl text-[14px] font-semibold tracking-wide transition-all duration-300 hover:-translate-y-px disabled:opacity-40 disabled:translate-y-0"
                    style={{
                      background: 'linear-gradient(180deg, #E6C86A 0%, #D4AF37 60%, #B88B1A 100%)',
                      color: '#0B0F1A',
                      boxShadow: '0 6px 24px rgba(212,175,55,0.32), inset 0 1px 0 rgba(255,255,255,0.25)',
                    }}
                    data-testid="cv-approve-proposal-btn"
                  >
                    {approvingProposal ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-5 h-5" />}
                    {approvingProposal ? 'Approving...' : 'Approve & Continue to Contract'}
                  </button>
                  {!changesOpen ? (
                    <button
                      onClick={() => setChangesOpen(true)}
                      className="w-full mt-3 py-2.5 rounded-xl text-[12.5px] font-medium text-white/45 hover:text-white/70 transition-colors"
                      style={{ background: 'transparent', border: '1px solid rgba(255,255,255,0.10)' }}
                      data-testid="cv-request-changes-btn"
                    >
                      I&apos;d like some changes first
                    </button>
                  ) : (
                    <div className="mt-3 rounded-xl p-3.5" style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.10)' }}>
                      <label className="block text-[11px] text-white/40 mb-1.5">What would you like adjusted?</label>
                      <textarea
                        value={changesNote}
                        onChange={(e) => setChangesNote(e.target.value)}
                        rows={3}
                        placeholder="Tell us what you'd like changed — pricing, scope, dates, anything."
                        className="w-full bg-white/[0.03] border border-white/[0.10] rounded-lg px-3 py-2.5 text-[13px] text-white/75 placeholder:text-white/20 outline-none resize-none focus:border-[#D4AF37]/30"
                        data-testid="cv-changes-note"
                      />
                      <div className="flex items-center justify-end gap-2 mt-2.5">
                        <button onClick={() => { setChangesOpen(false); setChangesNote(''); }} className="px-3 py-1.5 rounded-lg text-[12px] text-white/40 hover:text-white/60 transition-colors" data-testid="cv-changes-cancel">Cancel</button>
                        <button
                          onClick={handleRequestChanges}
                          disabled={requestingChanges || !changesNote.trim()}
                          className="px-4 py-1.5 rounded-lg text-[12px] font-semibold transition-all disabled:opacity-30"
                          style={{ background: 'rgba(212,175,55,0.12)', border: '1px solid rgba(212,175,55,0.28)', color: '#E7C75E' }}
                          data-testid="cv-submit-changes-btn"
                        >
                          {requestingChanges ? 'Sending…' : 'Send request'}
                        </button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        )}

        {/* ── PROPOSAL APPROVAL ──
            When the enhancements store is on screen it owns the ONE pinned
            bottom bar, and these actions live in the review step instead. */}
        {activeDoc === 'proposal' && docData && !(!proposalApproved && !proposalChangesRequested && !data.readOnly && !data.hideAddOnsFromClient) && (
          <div
            className={`mt-6 sticky bottom-0 z-30 pt-4 pb-4${cvmOn ? ' cvm-hide-mobile' : ''}`}
            style={{
              // Solid ground so proposal content cannot show through the
              // buttons as it scrolls underneath.
              background: 'linear-gradient(180deg, rgba(8,10,20,0) 0%, #080A14 22%, #080A14 100%)',
            }}
            data-testid="cv-proposal-action-area"
          >
            {proposalApproved ? (
              <ApprovedBadge label="Proposal" timestamp={data.proposalApprovedAt} />
            ) : proposalChangesRequested ? (
              <div
                className="rounded-xl px-5 py-4 text-center"
                style={{ background: 'rgba(245,158,11,0.06)', border: '1px solid rgba(245,158,11,0.25)' }}
                data-testid="cv-proposal-changes-requested"
              >
                <p className="text-[13px] font-semibold text-amber-300/90">Thanks — we&apos;ve got your notes</p>
                <p className="text-[12px] text-white/50 mt-1 leading-relaxed">
                  We&apos;re reviewing the changes you asked for and will follow up shortly with an updated proposal.
                </p>
              </div>
            ) : data.readOnly ? null : (
              <>
                {data.proposalRevisedAt && (
                  <div
                    className="mb-3 rounded-lg px-4 py-3 text-center"
                    style={{ background: 'rgba(52,211,153,0.06)', border: '1px solid rgba(52,211,153,0.22)' }}
                    data-testid="cv-proposal-revised-banner"
                  >
                    <p className="text-[12.5px] font-semibold text-emerald-300/90">
                      {data.proposalRevisionNote
                        ? `Updated proposal \u2014 ${data.proposalRevisionNote}`
                        : 'Updated proposal'}
                    </p>
                    <p className="text-[11.5px] text-white/50 mt-1 leading-relaxed">
                      This replaces the version you saw earlier. Have a look, then approve below when
                      it looks right.
                    </p>
                  </div>
                )}
                <button
                  onClick={() => setReviewOpen(true)}
                  className="w-full flex items-center justify-center gap-2 py-4 rounded-xl text-[14px] font-semibold tracking-wide transition-all duration-300 hover:-translate-y-px"
                  style={{
                    background: 'linear-gradient(180deg, #E6C86A 0%, #D4AF37 60%, #B88B1A 100%)',
                    color: '#0B0F1A',
                    boxShadow: '0 6px 24px rgba(212,175,55,0.32), inset 0 1px 0 rgba(255,255,255,0.25)',
                  }}
                  data-testid="cv-proposal-review-open-btn"
                >
                  Review my experience
                  <ArrowRight className="w-4 h-4" />
                </button>
                {!changesOpen ? (
                  <button
                    onClick={() => setChangesOpen(true)}
                    className="w-full mt-3 py-2.5 rounded-xl text-[12.5px] font-medium text-white/45 hover:text-white/70 transition-colors"
                    style={{ background: 'transparent', border: '1px solid rgba(255,255,255,0.10)' }}
                    data-testid="cv-request-changes-btn"
                  >
                    I&apos;d like some changes first
                  </button>
                ) : (
                  <div className="mt-3 rounded-xl p-3.5" style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.10)' }}>
                    <label className="block text-[11px] text-white/40 mb-1.5">What would you like adjusted?</label>
                    <textarea
                      value={changesNote}
                      onChange={(e) => setChangesNote(e.target.value)}
                      rows={3}
                      placeholder="Tell us what you'd like changed — pricing, scope, dates, anything."
                      className="w-full bg-white/[0.03] border border-white/[0.10] rounded-lg px-3 py-2.5 text-[13px] text-white/75 placeholder:text-white/20 outline-none resize-none focus:border-[#D4AF37]/30"
                      data-testid="cv-changes-note"
                    />
                    <div className="flex items-center justify-end gap-2 mt-2.5">
                      <button onClick={() => { setChangesOpen(false); setChangesNote(''); }} className="px-3 py-1.5 rounded-lg text-[12px] text-white/40 hover:text-white/60 transition-colors" data-testid="cv-changes-cancel">Cancel</button>
                      <button
                        onClick={handleRequestChanges}
                        disabled={requestingChanges || !changesNote.trim()}
                        className="px-4 py-1.5 rounded-lg text-[12px] font-semibold transition-all disabled:opacity-30"
                        style={{ background: 'rgba(212,175,55,0.12)', border: '1px solid rgba(212,175,55,0.28)', color: '#E7C75E' }}
                        data-testid="cv-submit-changes-btn"
                      >
                        {requestingChanges ? 'Sending…' : 'Send request'}
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* ── CONTRACT APPROVAL + SIGNATURE INTENT ── */}
        {activeDoc === 'contract' && docData && (
          <div className="mt-6" data-testid="cv-contract-action-area">
            {contractApproved || data.readOnly ? (
              <div />
            ) : (
              <div
                className="rounded-xl overflow-hidden"
                style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)' }}
              >
                <div className="px-5 py-4 flex items-center justify-between gap-3" style={{ borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
                  <p className="text-[11px] text-white/30 tracking-widest uppercase font-medium">Approval & Signature</p>
                </div>
                <div className="px-0 sm:px-5 py-5 space-y-4">
                  {/* Signing as (context) */}
                  <div className="rounded-xl px-4 py-3 flex items-start gap-3" style={{ background: 'rgba(212,175,55,0.03)', border: '1px solid rgba(212,175,55,0.08)' }}>
                    <div className="w-8 h-8 rounded-full flex items-center justify-center shrink-0 mt-0.5" style={{ background: 'rgba(212,175,55,0.08)' }}>
                      <PenTool className="w-3.5 h-3.5 text-[#D4AF37]/60" />
                    </div>
                    <div className="flex-1">
                      <p className="text-[11px] text-white/40 mb-0.5">You are signing as</p>
                      <p className="text-[14px] text-white/80 font-medium">{data.event?.fullName || 'Client'}</p>
                      <p className="text-[10px] text-white/25 mt-1">Your IP address and signing time will be recorded in the signature audit trail.</p>
                    </div>
                  </div>

                  {/* Checkbox */}
                  <label className="flex items-start gap-3 cursor-pointer group" data-testid="cv-agree-checkbox-label">
                    <div className="mt-0.5 shrink-0">
                      <input
                        type="checkbox"
                        checked={agreedTerms}
                        onChange={e => setAgreedTerms(e.target.checked)}
                        className="sr-only"
                        data-testid="cv-agree-checkbox"
                      />
                      <div
                        className="w-5 h-5 rounded-md flex items-center justify-center transition-all duration-200"
                        style={{
                          background: agreedTerms ? 'rgba(34,197,94,0.2)' : 'rgba(255,255,255,0.05)',
                          border: `1px solid ${agreedTerms ? 'rgba(34,197,94,0.4)' : 'rgba(255,255,255,0.1)'}`,
                        }}
                      >
                        {agreedTerms && <Check className="w-3 h-3 text-[#22C55E]" />}
                      </div>
                    </div>
                    <span className="text-[13px] text-white/50 leading-relaxed group-hover:text-white/60 transition-colors">
                      I have reviewed and agree to the contract terms
                    </span>
                  </label>

                  {/* Hybrid Signature Pad (Type / Draw) */}
                  <SignaturePad
                    clientName={data.event?.fullName || ''}
                    onChange={({ signerName: n, signatureImage: img, signatureMethod: m }) => {
                      setSignerName(n);
                      setSignatureImage(img);
                      setSignatureMethod(m);
                    }}
                    disabled={approvingContract}
                  />

                  {/* Legal disclosure */}
                  <p className="text-[10px] text-white/25 leading-relaxed" data-testid="cv-legal-disclosure-pre">
                    By signing, you agree this electronic signature is the legal equivalent of a handwritten signature under the U.S. E-SIGN Act. A copy will be emailed to you with a signature certificate.
                  </p>

                  {/* Error */}
                  {contractError && (
                    <p className="text-[12px] text-red-400/70" data-testid="cv-contract-error">{contractError}</p>
                  )}
                  {!signingTotalResolved && !contractError && (
                    /* Never let a client sign against an unknown number — but
                       never strand them either: show what IS known and a way
                       to reach us. */
                    <div data-testid="cv-contract-total-unresolved">
                      <p className="text-[12px] text-amber-400/80">
                        We can&apos;t confirm your total right now, so signing is paused — nothing has been recorded.
                      </p>
                      {(() => {
                        const sub = (data.event && data.event.contractAmount) ?? (data.proposal && data.proposal.totalAmount);
                        return sub != null ? (
                          <p className="text-[12px] text-white/55 mt-1.5" data-testid="cv-contract-known-subtotal">
                            Known subtotal: <span className="text-white/80 font-semibold">{formatMoney(sub)}</span>
                          </p>
                        ) : null;
                      })()}
                      <button
                        type="button"
                        onClick={() => { setChangesOpen(true); }}
                        className="mt-2 text-[12px] text-[#E6C86A] underline underline-offset-2"
                        data-testid="cv-contract-message-us"
                      >
                        Message us
                      </button>
                    </div>
                  )}

                  {/* Submit */}
                  <button
                    onClick={handleApproveContract}
                    disabled={approvingContract || !agreedTerms || !signatureImage || !signerName.trim() || !signingTotalResolved}
                    className="w-full flex items-center justify-center gap-2 py-3.5 rounded-xl text-[13px] font-semibold tracking-wide transition-all duration-300 hover:-translate-y-px disabled:opacity-40 disabled:translate-y-0 disabled:cursor-not-allowed"
                    style={{
                      background: 'linear-gradient(160deg,#3BE0A6,#1FB184)',
                      border: '1px solid transparent',
                      color: '#052016',
                      boxShadow: '0 10px 26px rgba(47,177,132,0.28)',
                    }}
                    data-testid="cv-approve-contract-btn"
                  >
                    {approvingContract ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <PenTool className="w-4 h-4" />
                    )}
                    {approvingContract ? 'Signing...' : 'Approve & Sign → Payment'}
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* ── Phase B — contract-stage live upgrade (after the signature panel) ──
            Signing comes before shopping. The same per-event hidden list the
            proposal section uses is passed here too — without it the contract
            tab rendered the entire snapshot and the admin's show/hide choices
            had no effect at the signature stage. */}
        {activeDoc === 'contract' && docData && !contractApproved && !data.hideAddOnsFromClient && (
          <SectionErrorBoundary section="contract-upgrades">
            <ContractUpgrades
              token={token}
              baseAmount={data.event?.contractAmount}
              addOns={data.proposalAddOns}
              liveAddOns={data.liveAddOns || []}
              hiddenKeys={data.clientHiddenAddOns || []}
              featuredKeys={data.clientFeaturedAddOns ?? null}
              shownKeys={data.clientShownAddOns || []}
              initialSelected={data.selectedPackageAddOns}
              serverAddOnsTotal={data.contract?.packageAddOnsTotal}
              serverNewTotal={data.contract?.packageNewTotal}
              onTotalsChanged={(t) => {
                // The signing figure follows the SERVER's answer, immediately.
                if (t && t.newTotal != null) setSigningTotalOverride(Number(t.newTotal));
              }}
            />
          </SectionErrorBoundary>
        )}

        {/* ── PRINT ONLY ── An unsigned contract prints with a blank signature
            and date line, the way a paper agreement is handed over. The signed
            view already prints its signature, audit line and E-SIGN notice. */}
        {activeDoc === 'contract' && docData && !contractApproved && (
          <div className="cvp-print-only cvp-print-signline" aria-hidden="true">
            <div style={{ fontSize: '8pt', textTransform: 'uppercase', letterSpacing: '.12em' }}>Signatures</div>
            <div className="row">Client signature<span className="rule" /></div>
            <div className="row">Printed name<span className="rule" /></div>
            <div className="row">Date<span className="rule" /></div>
            <div className="row" style={{ fontSize: '8.5pt' }}>
              By signing, both parties agree this electronic signature is the legal equivalent of a
              handwritten signature under the U.S. E-SIGN Act. The signer&apos;s IP address and signing
              time are recorded in the signature audit trail.
            </div>
          </div>
        )}

        {/* Print sits at the very END — the client signs first, then takes a
            copy away. Prints the SAME server document that gets emailed and
            downloaded; the browser-print path used to render a separate React
            copy of the contract. */}
        {activeDoc === 'contract' && docData && !contractApproved && !data.readOnly && (
          <div className="mt-8 flex justify-center no-print">
            <button
              onClick={printContractDocument}
              className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-[11px] font-medium transition-all hover:-translate-y-px"
              style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)', color: 'rgba(255,255,255,0.35)' }}
              data-testid="cv-print-unsigned-contract-btn"
            >
              <Printer className="w-3 h-3" />
              Print
            </button>
          </div>
        )}

        {/* Footer */}
        <div className="mt-10 pt-6 text-center" style={{ borderTop: '1px solid rgba(255,255,255,0.04)' }}>
          <div className="flex items-center justify-center gap-2 text-[11px] text-white/15">
            <CheckCircle2 className="w-3.5 h-3.5" />
            <span>Powered by {brandName}</span>
          </div>
        </div>
      </main>

      {/* Client Message Widget */}
      <div className="no-print">
        <ClientMessageWidget token={token} />
      </div>
    </div>
  );
}


// ══════════════════════════════════════════════════════════════
// Client Payment View — Premium payment experience
// ══════════════════════════════════════════════════════════════

function _fmtLongDate(iso) {
  if (!iso) return '';
  return formatDate(iso);
}

// Client-facing date render (paid receipts + the balance-due banner). Hoisted
// to module scope and exported so the rule is unit-testable — the values it
// receives come straight from production records.
export const _receiptDate = (iso) => {
  if (!iso) return '';
  return formatDate(iso);
};


function ClientPaymentView({ eventId, portalToken, paymentIncentive, eventDate }) {
  const [payments, setPayments] = useState([]);
  // BLOCK S28 — the server's ONE money truth (utils/payment_truth), kept as
  // received so the Payments tab reads it instead of re-adding rows in the
  // browser. Null until a successful /payments fetch; cleared with payments.
  const [paymentSummary, setPaymentSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  // Three states, not two. "Empty" must mean the server SAID zero rows —
  // a failed load is not "no payments due" for a client who owes a retainer.
  const [payError, setPayError] = useState(false);
  const [processing, setProcessing] = useState(null);
  const [payInfo, setPayInfo] = useState({ zelleInfo: {}, checkInfo: {} });
  const [payMobileMethod, setPayMobileMethod] = useState('zelle');

  const fetchPayments = useCallback(async () => {
    // No token = we cannot ask, so we do not know. Never render "nothing due".
    if (!portalToken) { setPayError(true); setLoading(false); return; }
    setLoading(true);
    setPayError(false);
    try {
      // Token-gated client-portal endpoint (no admin session needed).
      const payRes = await fetch(`${API}/api/leads/client-view/${portalToken}/payments`);
      if (!payRes.ok) {
        setPayError(true);
        return;
      }
      const payData = await payRes.json();
      setPayments(payData.payments || []);
      setPaymentSummary({
        totalDue: payData.totalDue,
        totalPaid: payData.totalPaid,
        totalRefunded: payData.totalRefunded,
        outstanding: payData.outstanding,
        settled: payData.settled,
        paidInFull: payData.paidInFull,
        nothingScheduled: payData.nothingScheduled,
        nextPayment: payData.nextPayment ?? null,
        isFullPayment: payData.isFullPayment,
      });
      // externalInfo intentionally ignored — the "Pay with Stripe" external
      // link is disabled in the new focused layout.
      setPayInfo({ zelleInfo: payData.zelleInfo || {}, checkInfo: payData.checkInfo || {} });
    } catch {
      setPayError(true);
    }
    finally { setLoading(false); }
  }, [portalToken]);

  useEffect(() => { fetchPayments(); }, [fetchPayments]);

  // Nothing happens on picking a method — no redirect, no instructions, no
  // record, no email — until this is confirmed. A misclick used to be a wrong
  // payment; now it is one dialog and a "Go back".
  const [confirmPay, setConfirmPay] = useState(null);

  const requestPay = (payment, method, amount, label) => {
    setConfirmPay({ payment, method, amount, label });
  };

  const handlePay = async (payment, method) => {
    setProcessing(payment.paymentId);
    try {
      const originUrl = window.location.origin;
      const res = await fetch(`${API}/api/payments/client/${payment.token}/pay`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ method, originUrl }),
      });
      const data = await res.json();
      if (method === 'card' && data.checkoutUrl) {
        window.location.href = data.checkoutUrl;
        return;
      }
      toast.success(method === 'zelle'
        ? "Zelle payment noted — we'll confirm receipt shortly."
        : "Check noted — we'll confirm once received.");
      fetchPayments();
    } catch { toast.error('Something went wrong. Please try again.'); }
    finally { setProcessing(null); }
  };

  const confirmSelectedMethod = async () => {
    if (!confirmPay) return;
    const { payment, method } = confirmPay;
    setConfirmPay(null);
    await handlePay(payment, method);
  };

  // A WAY BACK. Picking Zelle or check used to be a one-way switch: the row
  // went to pending_verification and the payment options never came back. This
  // reverses the selection server-side (existing endpoint — no email, the owner
  // gets an in-app notice and an audit entry) and reloads, so the options
  // render exactly as they first appeared. Works however often they change
  // their mind.
  const [undoing, setUndoing] = useState(null);
  const undoMethod = async (payment) => {
    setUndoing(payment.paymentId);
    try {
      const res = await fetch(`${API}/api/payments/client/${payment.token}/undo-method`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });
      if (!res.ok) throw new Error('undo_failed');
      await fetchPayments();
    } catch {
      toast.error("Couldn't change that. Please try again.");
    } finally {
      setUndoing(null);
    }
  };

  // Single Stripe checkout for the ENTIRE outstanding total (retainer + balance).
  // Backend `/pay-full` stamps every unpaid row with the same session id, so
  // the webhook flips them all to `paid` in one atomic pass.
  const handlePayFull = async () => {
    const firstUnpaid = payments.find((p) => p.status !== 'paid' && p.status !== 'confirmed');
    if (!firstUnpaid) return;
    setProcessing('pay-full');
    try {
      const originUrl = window.location.origin;
      const res = await fetch(`${API}/api/payments/client/${firstUnpaid.token}/pay-full`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ originUrl }),
      });
      const data = await res.json();
      if (data.checkoutUrl) { window.location.href = data.checkoutUrl; return; }
      throw new Error('missing_checkout_url');
    } catch { toast.error('Something went wrong. Please try again.'); }
    finally { setProcessing(null); }
  };

  if (loading) {
    return (
      <div className="space-y-4" data-testid="client-payments-loading">
        <div className="h-24 rounded-xl animate-pulse" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }} />
        <div className="h-40 rounded-xl animate-pulse" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }} />
        <div className="flex items-center justify-center pt-2"><Loader2 className="w-5 h-5 text-[#D4AF37]/30 animate-spin" /></div>
      </div>
    );
  }

  // The load failed. Say so — never imply the balance is settled.
  if (payError) {
    return (
      <div
        className="rounded-xl px-6 py-8 text-center"
        style={{ background: 'rgba(248,113,113,0.06)', border: '1px solid rgba(248,113,113,0.20)' }}
        data-testid="client-payments-error"
      >
        <p className="text-[13px] text-white/75 font-medium">We couldn&apos;t load your payment details just now.</p>
        <p className="text-[11.5px] text-white/40 mt-1.5">This is a connection problem on our side, not a change to what you owe.</p>
        <button
          type="button"
          onClick={fetchPayments}
          className="mt-4 rounded-full px-5 py-2 text-[12.5px] font-semibold text-[#D4AF37] border border-[#D4AF37]/40 hover:bg-[#D4AF37]/10 transition-colors"
          data-testid="client-payments-retry-btn"
        >
          Retry
        </button>
      </div>
    );
  }

  if (payments.length === 0) {
    return (
      <div className="text-center py-16">
        <DollarSign className="w-7 h-7 text-white/8 mx-auto mb-3" />
        <p className="text-[13px] text-white/25 font-medium">No payments due at this time</p>
        <p className="text-[11px] text-white/12 mt-1">Your payment details are being prepared by our team.</p>
      </div>
    );
  }

  // A single null/garbage amount used to poison every total into "$NaN".
  // Coerce per row and skip anything non-finite; the row still renders.
  const amountOf = (p) => {
    const n = Number(p?.amount);
    return Number.isFinite(n) ? n : 0;
  };
  const fmt = (amt) => {
    const n = Number(amt);
    return formatMoney(Number.isFinite(n) ? n : 0);
  };
  // BLOCK S28 — the money truth comes from the server's summary. Per-field
  // fallback to today's row arithmetic ONLY when the summary is missing or a
  // field is not a finite number (old cached response / failed parse), so the
  // tab never shows NaN or blank. amountOf/fmt and per-row rendering unchanged.
  const _finite = (v) => (Number.isFinite(Number(v)) ? Number(v) : null);
  const _rowTotalDue = payments.reduce((s, p) => s + amountOf(p), 0);
  const _rowTotalPaid = payments.filter((p) => p.status === 'paid' || p.status === 'confirmed').reduce((s, p) => s + amountOf(p), 0);
  const totalDue = _finite(paymentSummary?.totalDue) ?? _rowTotalDue;
  const totalPaid = _finite(paymentSummary?.totalPaid) ?? _rowTotalPaid;
  const remaining = _finite(paymentSummary?.outstanding) ?? (_rowTotalDue - _rowTotalPaid);
  const allPaid = paymentSummary
    ? (Boolean(paymentSummary.paidInFull) && payments.length > 0)
    : (remaining <= 0 && payments.length > 0);
  const pct = totalDue > 0 ? Math.min(100, Math.max(0, (totalPaid / totalDue) * 100)) : 0;

  const retainer = payments.find((p) => p.type === 'retainer') || payments[0];
  const retainerDone = retainer && (retainer.status === 'paid' || retainer.status === 'confirmed');
  // Andrea's-bug guard (Feb 2026): the client's "date locked" state must
  // read CUMULATIVE dollars vs. the deposit target — never the specific
  // retainer-line's status. If a card payment happened to land on the
  // Final Balance row (see backend guard + earliest-due settlement), the
  // date is still legitimately secured as long as the paid total covers
  // the deposit amount.
  const depositAmount = Number(retainer?.amount) || 0;
  const dateLocked = allPaid || (depositAmount > 0 && totalPaid >= depositAmount - 0.005);
  // Paid/confirmed lines to render as compact "✓ Paid" receipt rows so
  // money the client has already sent is always acknowledged, no matter
  // which specific line the payment settled against.
  const paidLines = payments.filter((p) => p.status === 'paid' || p.status === 'confirmed');
  // The next unpaid line (earliest due) — the affirmative "next step"
  // banner points the client at this rather than a specific type.
  const nextUnpaid = payments
    .filter((p) => p.status !== 'paid' && p.status !== 'confirmed')
    .sort((a, b) => new Date(a.dueDate || 0) - new Date(b.dueDate || 0))[0];

  // ── ONE full-payment-vs-retainer determination for the WHOLE portal ──────
  // Sourced from the backend (compute_is_full_payment) and carried on the
  // payments payload, so the hero card, the Secure-checkout summary and the
  // parent's progress step / update card all read the SAME answer. A row-based
  // fallback keeps it correct if the flag is ever absent (older payload).
  const _PRIMARY_LINE = retainer;
  const _PAID_STATES = ['paid', 'confirmed', 'refunded', 'canceled', 'cancelled'];
  const _remainingAfterPrimary = payments
    .filter((pp) => pp && pp.paymentId !== _PRIMARY_LINE?.paymentId && !_PAID_STATES.includes(String(pp.status || '').toLowerCase()))
    .reduce((s, pp) => s + (Number(pp.amount) || 0), 0);
  const isFullPayment = (paymentSummary && typeof paymentSummary.isFullPayment === 'boolean')
    ? paymentSummary.isFullPayment
    : (_remainingAfterPrimary <= 0.005);

  // WHAT is outstanding, not merely whether anything is. Every status
  // sentence on this tab reads from this one object.
  const status = paymentStatusFrom(payments, { totalPaid, totalDue, isFullPayment });

  // EVERY outstanding line, in due order — nothing hidden. `blockedReason`
  // carries the server's retainer-before-balance rule into words on the row
  // instead of hiding the row or rendering a control that would be refused.
  const outstandingRows = payments
    .filter((p) => (
      p.status !== 'paid'
      && p.status !== 'confirmed'
      && p.status !== 'pending_verification'
    ))
    .sort((a, b) => new Date(a.dueDate || 0) - new Date(b.dueDate || 0))
    .map((p) => ({
      row: p,
      blockedReason: (!dateLocked && p.type !== 'retainer')
        ? 'Payable once the retainer is settled.'
        : null,
    }));
  const payableRows = outstandingRows.filter((r) => !r.blockedReason).map((r) => r.row);

  // The hero pay card shows the retainer AND folds every other outstanding
  // line into its "Balance" row, so anything it already represents must not be
  // rendered a second time as a standalone charge block below it. A genuinely
  // additional item (its own label, its own figure) still gets its own block.
  const primaryCardShown = !!(
    !allPaid && !dateLocked && retainer && !retainerDone
    && retainer.status !== 'pending_verification'
  );
  const _SETTLED_ST = ['paid', 'confirmed', 'refunded', 'canceled', 'cancelled'];
  const primaryBalanceTotal = payments
    .filter((p) => p && p.paymentId !== retainer?.paymentId
      && !_SETTLED_ST.includes(String(p.status || '').toLowerCase()))
    .reduce((s, p) => s + (Number(p.amount) || 0), 0);
  const isShownInPayCard = (row) => {
    if (!primaryCardShown || !row) return false;
    if (row.paymentId === retainer?.paymentId) return true;
    if (row.type === 'retainer' || row.type === 'balance') return true;
    const label = String(row.label || '').trim().toLowerCase();
    if (label.startsWith('retainer') || label.startsWith('deposit')
      || label.startsWith('balance') || label.startsWith('final balance')) return true;
    const amt = Number(row.amount) || 0;
    return amt > 0 && (
      Math.abs(amt - (Number(retainer?.amount) || 0)) < 0.005
      || Math.abs(amt - primaryBalanceTotal) < 0.005
    );
  };

  const feeFor = (p) => (p.feePassthrough ? (p.estimatedProcessingFee ?? p.feeAmount ?? 0) : 0);
  const cardTotalFor = (p) => (p.feePassthrough ? (p.grossChargeAmount ?? (p.amount + feeFor(p))) : p.amount);
  const zelleHandle = payInfo.zelleInfo?.handle || payInfo.zelleInfo?.name || '';
  const copyZelle = async (handle) => {
    if (!handle) return;
    try {
      await navigator.clipboard.writeText(handle);
      toast.success('Zelle handle copied');
    } catch {
      toast.error("Your browser blocked the copy — the details are on screen above.");
    }
  };

  // The ONE entry point for both options on every row. It opens the existing
  // confirmation card, and when it is handed something it cannot act on it
  // says so and reports it — a visible, enabled control on this screen may
  // never do nothing.
  const chooseMethod = (row, method, amount) => {
    const target = payments.find((p) => p.paymentId === row?.paymentId);
    if (!target) {
      toast.error("We couldn't find that payment. Please refresh and try again.");
      Sentry.captureMessage('client portal: pay option fired with no payment row', {
        level: 'error',
        extra: { eventId, method, paymentCount: payments.length },
      });
      return;
    }
    if (!payableRows.some((p) => p.paymentId === target.paymentId)) {
      toast.error(
        !dateLocked && target.type !== 'retainer'
          ? 'Your retainer needs to be settled first — that one is ready above.'
          : "This payment can't be completed online right now. Please contact us and we'll sort it out.",
      );
      Sentry.captureMessage('client portal: pay option on a non-payable row', {
        level: 'error',
        extra: {
          eventId, method, paymentId: target.paymentId,
          type: target.type, status: target.status, dateLocked,
        },
      });
      return;
    }
    requestPay(target, method, amount, target.label || (target.type === 'retainer' ? 'Retainer' : 'Final balance'));
  };

  const pendingVerification = payments.filter((p) => p.status === 'pending_verification');
  const paidRemainingHint = totalDue - totalPaid;
  const cvmPayOn = payableRows.length > 0;

  return (
    <>
      {/* ── Mobile payment screen (approved spec) — the retainer figure, the
          method select and the one sticky action. Same payment rows and the
          same chooseMethod entry point as the desktop view below. */}
      {(() => {
        const row = payableRows.find((p) => p.type === 'retainer') || payableRows[0];
        if (!row) return null;
        const fee = Number(feeFor(row)) || 0;
        const cardTotal = Number(cardTotalFor(row)) || Number(row.amount) || 0;
        const later = outstandingRows.map((r) => r.row).find((p) => p.paymentId !== row.paymentId);
        const dueLabel = (iso) => {
          const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso || '');
          if (!m) return '';
          return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]))
            .toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
        };
        const amt = payMobileMethod === 'card' ? cardTotal : Number(row.amount) || 0;
        return (
          <div className="cvm" data-testid="cvm-payment-panels">
            <DueCard amount={formatMoney(Number(row.amount) || 0)} label={`${row.type === 'retainer' ? 'Retainer' : (row.label || 'Payment')} due now`} />
            <PayMethods
              zelleAmount={formatMoney(Number(row.amount) || 0)}
              cardAmount={formatMoney(cardTotal)}
              feeAmount={fee ? formatMoney(fee) : ''}
              selected={payMobileMethod}
              onSelect={setPayMobileMethod}
            />
            <PaymentSchedule
              retainer={formatMoney(Number(row.amount) || 0)}
              balance={later ? formatMoney(Number(later.amount) || 0) : ''}
              balanceDue={later ? dueLabel(later.dueDate) : ''}
            />
            <MobileStickyBar
              label={`Pay ${formatMoney(amt)} ${row.type === 'retainer' ? 'retainer' : 'now'}`}
              onPrimary={() => chooseMethod(row, payMobileMethod, amt)}
              quiet="Have a question first?"
              onQuiet={() => { window.location.hash = 'message'; }}
              testid="cvm-pay-btn"
            />
          </div>
        );
      })()}
    <div className={`space-y-8${cvmPayOn ? ' cvm-hide-mobile' : ''}`} data-testid="client-payments">

      {/* The SECURE CHECKOUT banner is gone: it repeated the retainer amount,
          the outstanding total and the trust row that the single pay card
          below already carries. */}

      {/* ── 0. PAID IN FULL — the whole thing is settled ── */}
      {allPaid && (
        <div
          className="rounded-xl px-6 py-6 text-center animate-[fadeIn_0.6s_ease-out]"
          style={{
            background: 'linear-gradient(135deg, rgba(34,197,94,0.04), rgba(34,197,94,0.01))',
            border: '1px solid rgba(34,197,94,0.1)',
            boxShadow: '0 0 40px rgba(34,197,94,0.04)',
          }}
          data-testid="paid-in-full-banner"
        >
          <div className="w-11 h-11 rounded-full mx-auto mb-3 flex items-center justify-center" style={{ background: 'rgba(34,197,94,0.08)', border: '1.5px solid rgba(34,197,94,0.2)' }}>
            <CheckCircle2 className="w-5 h-5 text-[#22C55E]/70" />
          </div>
          <p className="text-[15px] font-semibold text-[#22C55E]/75">You&apos;re all set. Everything is confirmed for your event.</p>
          <p className="text-[13px] text-white/55 mt-2" data-testid="cv-paid-in-full-line">
            Paid in full — {fmt(totalPaid)} received. Nothing further is due.
          </p>
          <p className="text-[11px] text-white/20 mt-1.5">We&apos;re excited to bring your experience to life.</p>
        </div>
      )}

      {/* ── 0b. DEPOSIT SECURED — total paid ≥ deposit target ──
          Handles the case where SOME money has arrived (enough to cover the
          deposit) but there's still a balance remaining. Feels affirmative
          and drops the "pay your retainer" panic. */}
      {!allPaid && dateLocked && (
        <div
          className="rounded-xl px-6 py-5 text-center animate-[fadeIn_0.6s_ease-out]"
          style={{
            background: 'linear-gradient(135deg, rgba(34,197,94,0.05), rgba(34,197,94,0.015))',
            border: '1px solid rgba(34,197,94,0.14)',
          }}
          data-testid="cv-date-secured-banner"
        >
          <div className="w-10 h-10 rounded-full mx-auto mb-2.5 flex items-center justify-center" style={{ background: 'rgba(34,197,94,0.10)', border: '1.5px solid rgba(34,197,94,0.24)' }}>
            <CheckCircle2 className="w-4 h-4 text-[#22C55E]/85" />
          </div>
          {/* Copy discipline (2026-08-08): the deposit DID secure the date — that
              part stays. But the banner must state the outstanding balance
              plainly on the SAME line, so the client never sees "thank you!"
              while a reminder about the same balance is landing in their inbox.
              No PAST DUE badge, no red — tone stays luxury, not collections. */}
          {status.state === 'extras_only' ? (
            // An add-on billed after the contract is NOT a contract balance.
            <p className="text-[14px] font-semibold text-[#22C55E]/85" data-testid="cv-date-secured-headline">
              Your date is secured and your contract is settled — {fmt(status.extrasOutstanding)}{' '}
              additional {status.extrasCount > 1 ? 'charges' : 'charge'} outstanding
            </p>
          ) : status.state === 'balance_outstanding' && nextUnpaid ? (
            <>
              <p className="text-[14px] font-semibold text-[#22C55E]/85" data-testid="cv-date-secured-headline">
                Your date is secured — {fmt(status.contractOutstanding)} contract balance remaining
                {nextUnpaid.dueDate ? <>, due {_receiptDate(nextUnpaid.dueDate)}</> : null}
              </p>
            </>
          ) : (
            <p className="text-[14px] font-semibold text-[#22C55E]/85" data-testid="cv-date-secured-headline">
              Your date is secured — thank you!
            </p>
          )}
        </div>
      )}

      {/* ── 0c. Paid receipt rows — acknowledge every dollar received ──
          Rendered as slim receipts so the client always sees money they've
          already sent, even when the corresponding schedule line was not
          the "expected" one. */}
      {paidLines.length > 0 && !allPaid && (
        <div className="space-y-2" data-testid="cv-paid-receipts">
          {paidLines.map((p) => (
            <div
              key={p.paymentId}
              className="rounded-xl px-4 py-3 flex items-center gap-3"
              style={{ background: 'rgba(34,197,94,0.035)', border: '1px solid rgba(34,197,94,0.14)' }}
              data-testid={`cv-paid-receipt-${p.type || p.paymentId}`}
            >
              <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(34,197,94,0.10)' }}>
                <CheckCircle2 className="w-4 h-4 text-[#22C55E]/85" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-[13px] font-semibold text-[#22C55E]/85">
                  {p.label || 'Payment'} · {fmt(p.amount)}
                </p>
                <p className="text-[11px] text-white/35 mt-0.5">
                  Paid{p.paidAt ? ` ${_receiptDate(p.paidAt)}` : ''}
                  {p.method ? ` · ${p.method === 'card' ? 'Card' : p.method === 'zelle' ? 'Zelle' : p.method === 'check' ? 'Check' : p.method}` : ''}
                </p>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ── 1. Slim progress line + bar ──
          Skipped while the retainer is the only thing due: the pay card below
          states that amount, so this line only said it a second time. */}
      {!allPaid && status.state !== 'retainer_unpaid' && (
        <div data-testid="cv-payments-progress" className="space-y-2.5">
          <div className="flex items-baseline justify-between gap-4">
            {/* An EXTRA CHARGE IS NOT "your remaining balance". With the
                contract settled this line used to describe a $1.00 add-on as
                the remaining balance on the booking. */}
            <p className="text-[12px] sm:text-[13px] text-white/55" data-testid="cv-progress-label">
              {status.state === 'extras_only'
                ? <>Additional {status.extrasCount > 1 ? 'charges' : 'charge'} outstanding: <span className="text-white/80 font-medium">{fmt(status.extrasOutstanding)}</span> — your contract is fully settled</>
                : status.state === 'balance_outstanding'
                  ? <>Remaining contract balance: <span className="text-white/80 font-medium">{fmt(status.contractOutstanding)}</span>{nextUnpaid?.dueDate ? <> — due {_receiptDate(nextUnpaid.dueDate)}</> : null}</>
                  : status.state === 'retainer_unpaid'
                    ? <>Your event total{depositAmount > 0 ? <> — <span className="text-white/80 font-medium">{fmt(depositAmount)}</span> {isFullPayment ? 'due now' : 'retainer due now'}</> : null}</>
                    : "You're all caught up"}
            </p>
            {/* Heading and figure from ONE source — `status.progress`. */}
            <p className="text-[11px] sm:text-[12px] text-white/45 tabular-nums flex-shrink-0" data-testid="cv-progress-figure">
              {status.progress.label}:{' '}
              <span className="text-white/80 font-medium">{fmt(status.progress.paid)}</span>{' '}
              of {fmt(status.progress.due)}
            </p>
          </div>
          <div className="h-1 w-full rounded-full overflow-hidden" style={{ background: 'rgba(255,255,255,0.05)' }}>
            <div
              className="h-full transition-all duration-700"
              style={{
                width: `${pct}%`,
                background: 'linear-gradient(90deg, #B88B1A 0%, #D4AF37 55%, #E8C979 100%)',
                boxShadow: '0 0 12px rgba(212,175,55,0.35)',
              }}
            />
          </div>
        </div>
      )}

      {/* ── 2. PRIMARY CARD — Zelle-first hero. Card is a demoted secondary link. ── */}
      {!allPaid && !dateLocked && retainer && !retainerDone && retainer.status !== 'pending_verification' && (() => {
        const GOLD='#D4AF37', IVORY='#F2ECDD', MINT='#8FBF9F', MUT='#8f9099', LINE='rgba(212,175,55,0.16)';
        const serif = { fontFamily: "'Playfair Display', serif" };
        const _amt = retainer.amount || 0;
        const _card = cardTotalFor(retainer);
        const _friendlyDate = eventDate ? formatDate(eventDate) : 'your date';
        // Reuse the portal-wide determination (isFullPayment) — do NOT recompute.
        const _PAID_ST = ['paid', 'confirmed', 'refunded', 'canceled', 'cancelled'];
        const _laterRows = payments
          .filter((pp) => pp && pp.paymentId !== retainer?.paymentId && !_PAID_ST.includes(String(pp.status || '').toLowerCase()))
          .sort((a, b) => new Date(a.dueDate || 0) - new Date(b.dueDate || 0));
        const _balanceRow = isFullPayment
          ? null
          : { amount: _laterRows.reduce((s, pp) => s + (Number(pp.amount) || 0), 0), dueLabel: _laterRows[0]?.dueDate ? _fmtLongDate(_laterRows[0].dueDate) : '' };
        // Split the amount into dollars + cents so the mockup's tabular
        // typography (small "$", inline cents) renders 1:1 without a
        // dedicated <Money> component.
        const _fmtParts = (v) => {
          const [d, c = '00'] = formatMoney(v || 0, { noSymbol: true }).split('.');
          return { dollars: d, cents: c };
        };
        const p = _fmtParts(_amt);
        return (
          <div
            style={{
              position: 'relative', overflow: 'hidden',
              borderRadius: 18, padding: '28px 30px',
              border: `1px solid ${LINE}`,
              background: 'radial-gradient(120% 80% at 100% 0%, rgba(212,175,55,0.10), transparent 55%), linear-gradient(180deg, rgba(212,175,55,0.03), rgba(255,255,255,0.012))',
              boxShadow: '0 24px 60px -34px rgba(212,175,55,0.45)',
            }}
            data-testid="cv-primary-deposit-card"
          >
            <div style={{ fontSize: 11, letterSpacing: '.24em', textTransform: 'uppercase', color: GOLD, fontWeight: 600 }} data-testid="cv-primary-eyebrow">
              {isFullPayment ? 'Payment due now' : 'Retainer due now'}
            </div>

            {/* ONE prominent figure. The NO FEE pill is gone — the Zelle tile
                in PayOptions already says "no fee" next to its own price. */}
            <div style={{ marginTop: 12 }}>
              <span
                style={{ ...serif, fontWeight: 600, fontSize: 46, color: '#E8C979', letterSpacing: '-.5px', lineHeight: 1 }}
                data-testid="cv-primary-deposit-total"
              >
                <span style={{ fontSize: 30, fontWeight: 500, color: '#D9BD62', marginRight: 2 }}>$</span>
                {p.dollars}
                <span style={{ fontSize: 22, fontWeight: 500, color: '#D9BD62', marginLeft: 2 }}>.{p.cents}</span>
              </span>
            </div>
            <div style={{ fontSize: 13.5, color: '#C9CBD3', marginTop: 11 }} data-testid="cv-primary-headline">
              {isFullPayment
                ? <>Pay in full to lock in {_friendlyDate}.</>
                : <>Pay your retainer to lock in your date.</>}
            </div>

            <LineItemsSummary
              items={retainer?.displayLineItems}
              totalLabel="Due now"
              totalAmount={_amt}
              balanceRow={_balanceRow}
              testPrefix="cv-primary-line-items"
            />

            {/* Both methods, both real prices, no accordion. */}
            <PayOptions
              row={retainer}
              zelleHandle={zelleHandle}
              cardTotal={_card}
              fee={feeFor(retainer)}
              onChoose={chooseMethod}
              onCopyZelle={copyZelle}
              processing={processing}
              checkInfo={payInfo.checkInfo}
              testPrefix="cv-primary-pay"
              earlyPay={paymentSummary?.nextPayment?.state === 'upcoming' && retainer?.paymentId === paymentSummary?.nextPayment?.paymentId}
            />

            {/* The one useful line from the deleted banner, as a footnote. */}
            <div
              style={{ fontSize: 11, color: MUT, marginTop: 12, textAlign: 'center', letterSpacing: '.02em' }}
              data-testid="cv-primary-trust-line"
            >
              Secure · Encrypted · Stripe-powered
            </div>
          </div>
        );
      })()}

      {/* ── 3. EVERY OUTSTANDING ROW — its own two options, its own amounts ──
          The accordions are gone. This block used to render ONE row behind two
          collapsed toggles ("Pay by Zelle" / "Pay by card"), so a client could
          not see the two real prices without clicking, and a second
          outstanding line had no control at all. Each row now carries its own
          card and Zelle options with its own figures, and a row the server
          would refuse says why in plain English instead of disappearing. */}
      {!allPaid && outstandingRows.map(({ row, blockedReason }) => {
        // Anything the hero pay card already shows — the retainer, the balance,
        // or a line that simply repeats one of those figures — is not rendered
        // again down here.
        if (isShownInPayCard(row)) return null;
        const MUT='#8f9099', MUT2='#66676f', SOFT='rgba(255,255,255,0.06)';
        const serif = { fontFamily: "'Playfair Display', serif" };
        const _dueLabel = row.dueDate ? _fmtLongDate(row.dueDate) : '';
        const _overdue = row.status === 'overdue';
        const bp = (() => {
          const [d, c = '00'] = formatMoney(row.amount || 0, { noSymbol: true }).split('.');
          return { dollars: d, cents: c };
        })();
        return (
          <div
            key={row.paymentId}
            style={{
              marginTop: 16, borderRadius: 18, padding: '24px 22px',
              background: 'rgba(255,255,255,0.022)', border: `1px solid ${SOFT}`,
            }}
            data-testid={`cv-payable-card-${row.paymentId}`}
          >
            <div style={{ fontSize: 11, letterSpacing: '.24em', textTransform: 'uppercase', color: MUT, fontWeight: 600 }}>
              {row.label || (row.type === 'balance' ? 'Final Balance' : 'Amount due')}
              {isExtraCharge(row) && (
                <span
                  style={{
                    fontSize: 10, letterSpacing: '.14em', color: '#E8C979',
                    background: 'rgba(212,175,55,0.08)', border: '1px solid rgba(212,175,55,0.22)',
                    padding: '3px 9px', borderRadius: 99, marginLeft: 8,
                  }}
                  data-testid={`cv-extra-charge-chip-${row.paymentId}`}
                >Additional charge</span>
              )}
              {blockedReason && (
                <span
                  style={{
                    fontSize: 10.5, color: MUT, background: 'rgba(255,255,255,0.05)',
                    border: `1px solid ${SOFT}`, padding: '3px 9px', borderRadius: 99, marginLeft: 8,
                  }}
                  data-testid={`cv-not-payable-chip-${row.paymentId}`}
                >Not payable yet</span>
              )}
            </div>
            <div style={{ marginTop: 12 }} data-testid={`cv-payable-amount-${row.paymentId}`}>
              <span style={{ ...serif, fontWeight: 600, fontSize: 28, color: '#D9D2C2', letterSpacing: '-.5px', lineHeight: 1 }}>
                <span style={{ fontSize: 19, fontWeight: 500, color: '#D9D2C2', marginRight: 2 }}>$</span>
                {bp.dollars}
                <span style={{ fontSize: 14, fontWeight: 500, color: '#D9D2C2', marginLeft: 2 }}>.{bp.cents}</span>
              </span>
            </div>
            <div style={{ fontSize: 12.5, color: MUT2, marginTop: 6 }}>
              {_dueLabel ? <>Due {_dueLabel}</> : 'No due date set'}
              {_overdue ? '' : ' · pay anytime before your event'}
            </div>
            {isExtraCharge(row) && (
              <div style={{ fontSize: 12.5, color: MUT, marginTop: 4 }} data-testid={`cv-extra-charge-note-${row.paymentId}`}>
                Billed in addition to your contract — not part of your contract balance.
              </div>
            )}

            <LineItemsSummary
              items={row?.displayLineItems}
              totalLabel="Due now"
              totalAmount={row.amount || 0}
              testPrefix={`cv-line-items-${row.paymentId}`}
            />

            <PayOptions
              row={row}
              zelleHandle={zelleHandle}
              cardTotal={cardTotalFor(row)}
              fee={feeFor(row)}
              onChoose={chooseMethod}
              onCopyZelle={copyZelle}
              processing={processing}
              blockedReason={blockedReason}
              checkInfo={payInfo.checkInfo}
              earlyPay={paymentSummary?.nextPayment?.state === 'upcoming' && row?.paymentId === paymentSummary?.nextPayment?.paymentId}
            />
          </div>
        );
      })}

      {/* ── 4. Pending-verification status (Zelle/Check waiting on confirmation) ── */}
      {pendingVerification.map((p) => (
        <div
          key={p.paymentId}
          className="rounded-xl px-5 py-4"
          style={{ background: 'rgba(59,130,246,0.05)', border: '1px solid rgba(59,130,246,0.15)' }}
          data-testid={`cv-pending-verification-${p.type}`}
        >
          <div className="flex items-center gap-3">
            <Clock className="w-4 h-4 text-[#3B82F6]/70 flex-shrink-0" />
            <div className="min-w-0">
              <p className="text-[13px] font-semibold text-white/80">
                {p.label} — {p.method === 'zelle' ? 'Zelle' : 'Check'} sent
              </p>
              <p className="text-[11px] text-white/40 mt-0.5">
                We&apos;ll confirm receipt and update your status shortly.
              </p>
              {(p.method === 'zelle' || p.method === 'check') && (
              <button
                type="button"
                onClick={() => undoMethod(p)}
                disabled={undoing === p.paymentId}
                className="mt-2 inline-flex items-center gap-1.5 text-[11px] text-white/35 hover:text-white/70 underline underline-offset-4 disabled:opacity-40 transition-colors"
                data-testid={`cv-choose-different-method-${p.type}`}
              >
                {undoing === p.paymentId ? <Loader2 className="w-3 h-3 animate-spin" /> : null}
                Choose a different way to pay
              </button>
              )}
            </div>
          </div>
        </div>
      ))}

      <ConfirmPaymentMethodDialog
        target={confirmPay}
        eventLabel={eventDate ? _fmtLongDate(eventDate) : ''}
        busy={!!processing}
        onCancel={() => setConfirmPay(null)}
        onConfirm={confirmSelectedMethod}
      />

    </div>
    </>
  );
}

/**
 * Small disclosure panel: Zelle handle + Check payee/address, each with a
 * copy link and a "I've sent this" confirm button that calls the same
 * `/pay` endpoint (Card/Zelle/Check are all valid methods there — the
 * server owns the state transition, so no dedicated "undo" call is needed).
 */
function OtherMethodsDisclosure({ payment, payInfo, onSelect, processing }) {
  const zelleEnabled = payInfo.zelleInfo?.enabled && (payInfo.zelleInfo?.handle || payInfo.zelleInfo?.name);
  const checkEnabled = payInfo.checkInfo?.enabled && (payInfo.checkInfo?.payableTo || payInfo.checkInfo?.address);

  if (!zelleEnabled && !checkEnabled) {
    return (
      <div
        className="mt-4 rounded-lg px-4 py-3"
        style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.05)' }}
        data-testid={`cv-other-methods-empty-${payment.type}`}
      >
        <p className="text-[12px] text-white/40">
          Card checkout is the only method available right now.
        </p>
      </div>
    );
  }

  return (
    <div
      className="mt-4 rounded-xl p-5 space-y-4"
      style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)' }}
      data-testid={`cv-other-methods-${payment.type}`}
    >
      {zelleEnabled && (
        <div>
          <div className="flex items-baseline gap-2 mb-2">
            <Building className="w-3.5 h-3.5 text-[#D4AF37]/60 self-center" />
            <p className="text-[11px] uppercase tracking-[0.2em] text-[#D4AF37]/75 font-medium">Zelle</p>
            <span className="text-[9.5px] text-white/25 uppercase tracking-wider">no fee</span>
          </div>
          {payInfo.zelleInfo?.handle && (
            <div className="flex items-baseline gap-2 pl-5.5">
              <p className="text-[13px] font-semibold text-white/85 tabular-nums">{payInfo.zelleInfo.handle}</p>
              <CopyLink text={payInfo.zelleInfo.handle} />
            </div>
          )}
          {payInfo.zelleInfo?.name && (
            <p className="text-[11px] text-white/45 pl-5.5 mt-0.5">{payInfo.zelleInfo.name}</p>
          )}
          <button
            type="button"
            onClick={() => onSelect('zelle')}
            disabled={processing}
            className="mt-3 ml-5.5 inline-flex items-center gap-1.5 text-[11.5px] font-medium text-white/55 hover:text-white/85 underline underline-offset-4 disabled:opacity-40 transition-colors"
            data-testid={`cv-zelle-select-${payment.type}`}
          >
            {processing ? <Loader2 className="w-3 h-3 animate-spin" /> : null}
            I&apos;ve sent this via Zelle
          </button>
        </div>
      )}

      {checkEnabled && (
        <div className={zelleEnabled ? 'pt-4' : ''} style={zelleEnabled ? { borderTop: '1px solid rgba(255,255,255,0.05)' } : {}}>
          <div className="flex items-baseline gap-2 mb-2">
            <Mail className="w-3.5 h-3.5 text-[#D4AF37]/60 self-center" />
            <p className="text-[11px] uppercase tracking-[0.2em] text-[#D4AF37]/75 font-medium">Check</p>
            <span className="text-[9.5px] text-white/25 uppercase tracking-wider">no fee</span>
          </div>
          {payInfo.checkInfo?.payableTo && (
            <div className="pl-5.5">
              <p className="text-[9.5px] uppercase text-white/32 tracking-wider">Payable to</p>
              <div className="flex items-baseline gap-2 mt-0.5">
                <p className="text-[13px] font-semibold text-white/85">{payInfo.checkInfo.payableTo}</p>
                <CopyLink text={payInfo.checkInfo.payableTo} />
              </div>
            </div>
          )}
          {payInfo.checkInfo?.address && (
            <div className="pl-5.5 mt-2">
              <p className="text-[9.5px] uppercase text-white/32 tracking-wider">Mail to</p>
              <p className="text-[12px] text-white/75 whitespace-pre-line mt-0.5">{payInfo.checkInfo.address}</p>
            </div>
          )}
          <button
            type="button"
            onClick={() => onSelect('check')}
            disabled={processing}
            className="mt-3 ml-5.5 inline-flex items-center gap-1.5 text-[11.5px] font-medium text-white/55 hover:text-white/85 underline underline-offset-4 disabled:opacity-40 transition-colors"
            data-testid={`cv-check-select-${payment.type}`}
          >
            {processing ? <Loader2 className="w-3 h-3 animate-spin" /> : null}
            I&apos;ve mailed this check
          </button>
        </div>
      )}
    </div>
  );
}

function CopyLink({ text }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      type="button"
      className="text-[10px] text-white/30 hover:text-[#D4AF37]/75 underline underline-offset-2 transition-colors"
      onClick={async (e) => {
        e.stopPropagation();
        try {
          await navigator.clipboard.writeText(text);
          setCopied(true);
          setTimeout(() => setCopied(false), 1500);
        } catch { /* clipboard not available */ }
      }}
      data-testid="cv-copy-link"
    >
      {copied ? 'copied' : 'copy'}
    </button>
  );
}
